import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, LogOut, MapPin, ArrowLeft, Layers, MousePointer, Download } from 'lucide-react';
import { useBoreholeBrowser } from '../hooks/useBoreholeBrowser';
import { useAuthContext } from '../contexts/AuthContext';
import { pl2000ToWGS84, wgs84ToPl2000 } from '../utils/coordConvert';
import { fetchBoreholeWithLayers } from '../firebase/boreholeBrowser';
import { prepareBoreholeData, interpolateVirtualBorehole, extractZoneBoundaries, generateInterpolationGrid, generateVoxelGrid, generateCrossSectionBoreholes, computeGridBoundingBox, computeGridCoordinates, classifyAtElevation } from '../utils/interpolation';
import { listTiles, getTile, deleteTile, bilinearSampleTile, findCoveringTile } from '../firebase/nmtTiles';
import { useNmtDownload } from '../contexts/NmtDownloadContext';
import Plot from 'react-plotly.js';
import PopupWindow from './PopupWindow';

const ZONE_COLORS = {
  grunt: '#8B6914',
  zwietrzelina: '#DAA520',
  skala: '#808080',
  woda: '#2196F3',
};

// Paleta dla tile NMT — różne kolory żeby odróżnić nakładające się obszary na mapie.
// Pomijamy pink (#ec4899) — zarezerwowany dla aktualnie rysowanego obszaru NMT.
const TILE_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#0ea5e9', '#a16207'];
const tileColorAt = (idx) => TILE_COLORS[idx % TILE_COLORS.length];

const COLORSCALE_OPTIONS = ['Rainbow', 'Jet', 'Viridis', 'RdBu', 'Hot', 'Plasma', 'Earth', 'Portland', 'Electric'];

// Domyślny zakres skali kolorów dla danych geofizycznych (oporność elektryczna typowo 200-800 Ω·m)
const GEOPHYS_DEFAULT_FILTER_MIN = 200;
const GEOPHYS_DEFAULT_FILTER_MAX = 800;
const GEOPHYS_DEFAULT_COLORSCALE = 'Rainbow';

const ZONE_LABELS = {
  grunt: 'Grunt',
  zwietrzelina: 'Zwietrzlina',
  skala: 'Skała',
};

// Sanitizer trace'ów dla Plotly 3D — gl-plot3d w `project()` crashuje na NaN/undefined/mismatched arrays
// w scatter3d ("Cannot read properties of undefined (reading '0')"). Filtruje problematyczne trace'y
// i loguje co znalazł — żeby znać gdzie w danych jest źródło, nie tylko że jest.
const sanitizePlotData = (rawData, plotLabel = 'Plot') => {
  const safe = [];
  rawData.forEach((trace, idx) => {
    if (!trace || typeof trace !== 'object') return;
    const label = `[${plotLabel} #${idx}] ${trace.type}/${trace.name || '(no name)'}`;
    if (trace.type === 'scatter3d') {
      const { x, y, z } = trace;
      if (!Array.isArray(x) || !Array.isArray(y) || !Array.isArray(z)) {
        console.warn('[sanitizePlotData] DROP', label, '— x/y/z nie są tablicami', { x, y, z });
        return;
      }
      if (x.length === 0) {
        console.warn('[sanitizePlotData] DROP', label, '— pusta tablica x');
        return;
      }
      if (x.length !== y.length || x.length !== z.length) {
        console.warn('[sanitizePlotData] DROP', label, `— rozjazd długości x=${x.length} y=${y.length} z=${z.length}`);
        return;
      }
      let badIdx = -1;
      for (let i = 0; i < x.length; i++) {
        if (!Number.isFinite(x[i]) || !Number.isFinite(y[i]) || !Number.isFinite(z[i])) { badIdx = i; break; }
      }
      if (badIdx !== -1) {
        console.warn('[sanitizePlotData] DROP', label, `— niepoprawna wartość na indeksie ${badIdx}`, { x: x[badIdx], y: y[badIdx], z: z[badIdx] });
        return;
      }
    } else if (trace.type === 'surface') {
      const { x, y, z } = trace;
      if (!Array.isArray(x) || !Array.isArray(y) || !Array.isArray(z)) {
        console.warn('[sanitizePlotData] DROP', label, '— x/y/z nie są tablicami', { x, y, z });
        return;
      }
      if (x.length === 0 || y.length === 0 || z.length === 0) {
        console.warn('[sanitizePlotData] DROP', label, `— pusta tablica (x=${x.length}, y=${y.length}, z=${z.length})`);
        return;
      }
      if (z.length !== y.length || (Array.isArray(z[0]) && z[0].length !== x.length)) {
        console.warn('[sanitizePlotData] DROP', label, `— rozjazd surface: x=${x.length} y=${y.length} z=${z.length}×${z[0]?.length}`);
        return;
      }
    }
    safe.push(trace);
  });
  if (safe.length !== rawData.length) {
    console.warn(`[sanitizePlotData ${plotLabel}] zostawiono ${safe.length}/${rawData.length} trace'ów`);
  }
  return safe;
};

export default function InterpolationMapPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();

  const {
    projects,
    loadingProjects,
    projectsError,
    projectsLoaded,
    loadProjects,
    projectFilter, setProjectFilter,
    selectedProject,
    selectProject,
    boreholes,
    allBoreholes,
    loadingBoreholes,
    boreholesError,
    boreholeFilter, setBoreholeFilter,
    selectedBoreholeIds,
    orderedSelection,
    toggleBoreholeSelection,
    clearSelection,
    selectAllBoreholes,
    addBoreholesByIds,
  } = useBoreholeBrowser();

  // Stan danych interpolacji
  const [boreholeData, setBoreholeData] = useState(null); // prepareBoreholeData result
  const [boreholeZoneInfo, setBoreholeZoneInfo] = useState({}); // {boreholeId: {hasGrunt, hasZwietrzelina, hasSkala, zones}}
  const [loadingData, setLoadingData] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [virtualBorehole, setVirtualBorehole] = useState(null); // wynik interpolacji
  const [clickCoords, setClickCoords] = useState(null); // {lat, lng, coordX, coordY}
  const [popupBlocked, setPopupBlocked] = useState(false); // popup zablokowany → fallback inline
  const [popupOpen, setPopupOpen] = useState(false); // czy popup z wirtualnym otworem jest aktualnie otwarty
  const popupWindowRef = useRef(null); // referencja do otwartego okna popup
  const popupBlockedRef = useRef(false); // mirror popupBlocked dla użycia w callbackach
  const [layerColors, setLayerColors] = useState({});

  // Grid 3D
  const [gridData, setGridData] = useState(null);
  const [gridResolution, setGridResolution] = useState(40);
  const [gridSizeMode, setGridSizeMode] = useState('points'); // 'points' = liczba punktów na bok, 'step' = krok w metrach
  const [gridStep, setGridStep] = useState(25); // krok siatki w metrach (tryb 'step')
  const [gridShapeMode, setGridShapeMode] = useState('square'); // 'square' = kwadrat z dłuższego boku, 'rect' = faktyczny prostokąt
  const [gridBuffer, setGridBuffer] = useState(20); // bufor w metrach z każdej strony bounding boxa
  const [gridMaskDistance, setGridMaskDistance] = useState(null); // maska: tnij komórki dalej niż X m od najbliższego otworu (null = bez maski)
  const [gridTopK, setGridTopK] = useState(null); // Top-K sąsiadów dla IDW (null = wszystkie otwory; obecne zachowanie). Łatwo cofnąć: usuń ten state + dropdown + przekazywanie do generatora.
  const [generatingGrid, setGeneratingGrid] = useState(false);
  const [wedgeDisplayMode, setWedgeDisplayMode] = useState('merge'); // 'merge' = zlanie, 'hole' = dziura
  const [aspectXY, setAspectXY] = useState(1.8);
  const [aspectZ, setAspectZ] = useState(1);

  // Cięcie poziome 3D — 2D plan view na zadanej rzędnej Z
  const [sliceOpen, setSliceOpen] = useState(false);
  const [sliceZ, setSliceZ] = useState(null); // rzędna m n.p.m. — inicjalizowana po wczytaniu gridu
  const [sliceUpsample, setSliceUpsample] = useState(1); // 1=bez wygładzania (piksele), 2/4/8 = bilinear resample N×
  const [sliceHeight, setSliceHeight] = useState(700); // wielkość mapy cięcia w pikselach (300-1400)

  // Teren z NMT (GUGiK) — pobiera realny teren z prefetchowanego tile zamiast IDW z otworów
  const [useNMTTerrain, setUseNMTTerrain] = useState(false);
  const [nmtTerrainError, setNmtTerrainError] = useState(null);
  const [gridTerrainSource, setGridTerrainSource] = useState(null); // { source: 'idw' | 'tile', tileStep?, tileFileName? }

  // Dane geofizyczne — chmury punktów X Y Z value wczytywane z plików .txt, in-memory (znikają po reload).
  // Per-plik: { id, fileName, x[], y[], z[], value[], min, max, count, pointSize, opacity, visible }
  // Wspólne dla wszystkich plików: skala kolorów + zakres (jeden colorbar na wykresie)
  const [geoPhysFiles, setGeoPhysFiles] = useState([]);
  const [geoPhysError, setGeoPhysError] = useState(null);
  const [geoPhysColorscale, setGeoPhysColorscale] = useState(GEOPHYS_DEFAULT_COLORSCALE);
  const [geoPhysFilterMin, setGeoPhysFilterMin] = useState(GEOPHYS_DEFAULT_FILTER_MIN);
  const [geoPhysFilterMax, setGeoPhysFilterMax] = useState(GEOPHYS_DEFAULT_FILTER_MAX);

  const handleGeoPhysUpload = useCallback(async (file) => {
    if (!file) return;
    setGeoPhysError(null);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/);
      const x = [], y = [], z = [], value = [];
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) continue;
        const parts = trimmed.split(/[\s,;\t]+/);
        if (parts.length < 4) continue;
        const px = parseFloat(parts[0]);
        const py = parseFloat(parts[1]);
        const pz = parseFloat(parts[2]);
        const pv = parseFloat(parts[3]);
        if (!Number.isFinite(px) || !Number.isFinite(py) || !Number.isFinite(pz) || !Number.isFinite(pv)) continue;
        x.push(px); y.push(py); z.push(pz); value.push(pv);
      }
      if (x.length === 0) {
        throw new Error('Nie znaleziono poprawnych wierszy (format: X Y Z wartość, separator spacja/tab)');
      }

      // Walidacja strefy PL-2000 vs otwory
      const fileZone = Math.floor(x[0] / 1000000);
      let projectZone = null;
      if (boreholeData && boreholeData.length > 0) {
        projectZone = Math.floor(boreholeData[0].y / 1000000);
      } else if (allBoreholes && allBoreholes.length > 0) {
        const first = allBoreholes.find(b => b.coordY);
        if (first) projectZone = Math.floor(first.coordY / 1000000);
      }
      if (projectZone !== null && fileZone !== projectZone) {
        setGeoPhysError(`⚠️ "${file.name}": plik w PL-2000 strefa ${fileZone}, projekt w strefie ${projectZone} — punkty mogą być daleko od modelu`);
      }

      let min = value[0], max = value[0];
      for (const v of value) { if (v < min) min = v; if (v > max) max = v; }

      setGeoPhysFiles(prev => [...prev, {
        id: `geo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        fileName: file.name,
        x, y, z, value, min, max, count: x.length,
        pointSize: 4,
        opacity: 0.8,
        visible: true,
      }]);
    } catch (err) {
      console.error('Błąd parsowania pliku geofizycznego:', err);
      setGeoPhysError(`"${file.name}": ${err.message || 'Nie udało się wczytać'}`);
    }
  }, [boreholeData, allBoreholes]);

  const updateGeoPhysFile = useCallback((id, updates) => {
    setGeoPhysFiles(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  }, []);

  const removeGeoPhysFile = useCallback((id) => {
    setGeoPhysFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  // Toggle: pokazywanie czerwonych polilinii w miejscach gdzie ślad profilu geofizycznego przecina warstwy geologiczne
  const [showProfileIntersections, setShowProfileIntersections] = useState(true);
  const [showBoreholeNames, setShowBoreholeNames] = useState(true); // etykiety nazw otworów na wykresie 3D

  // Filtrowane dane (po wspólnych progach min/max) — per plik, używane jako trace na wykresie
  const geoPhysTraces = useMemo(() => {
    const lo = geoPhysFilterMin ?? -Infinity;
    const hi = geoPhysFilterMax ?? Infinity;
    return geoPhysFiles.map(f => {
      if (!f.visible) return { ...f, x: [], y: [], z: [], value: [] };
      if (lo <= f.min && hi >= f.max) return f;
      const x = [], y = [], z = [], value = [];
      for (let i = 0; i < f.x.length; i++) {
        const v = f.value[i];
        if (v >= lo && v <= hi) { x.push(f.x[i]); y.push(f.y[i]); z.push(f.z[i]); value.push(v); }
      }
      return { ...f, x, y, z, value, filteredCount: x.length };
    });
  }, [geoPhysFiles, geoPhysFilterMin, geoPhysFilterMax]);

  // Tile NMT — prefetchowane obszary NMT zapisane w Firebase Storage
  const [tiles, setTiles] = useState([]); // listTiles() result
  const [tilesLoading, setTilesLoading] = useState(false);
  const [tileDrawMode, setTileDrawMode] = useState(false); // gdy true: drag prostokątem na mapie rysuje obszar NMT
  const [tileDrawBbox, setTileDrawBbox] = useState(null); // { minX, maxX, minY, maxY } w PL-2000
  const [tileDrawStep, setTileDrawStep] = useState(2); // krok m do pobrania
  // tileFetching i tileFetchProgress przeniesione do globalnego NmtDownloadContext (banner u góry)
  const [tileFetchError, setTileFetchError] = useState(null);

  // Efektywny bufor — bufor wyłączony (UI dropdowna zakomentowane). Dla obu trybów = 0.
  // Maska danych (convex hull + odległość) pełni teraz rolę "buforu wokół otworów".
  const effectiveBuffer = 0;

  // Bounding box obszaru gridu — uwzględnia wybrany kształt + bufor
  const gridBBox = useMemo(() => {
    if (!boreholeData || boreholeData.length === 0) return null;
    return computeGridBoundingBox(boreholeData, { shapeMode: gridShapeMode, bufferMeters: effectiveBuffer });
  }, [boreholeData, gridShapeMode, effectiveBuffer]);

  // Efektywne rozdzielczości X i Y — różne dla prostokąta przy trybie 'step'
  const { effectiveResX, effectiveResY } = useMemo(() => {
    if (!gridBBox) return { effectiveResX: gridResolution, effectiveResY: gridResolution };

    if (gridSizeMode === 'points') {
      // 'points' — N punktów na dłuższy bok, proporcjonalnie na krótszy (dla prostokąta)
      if (gridShapeMode === 'square' || gridBBox.rangeX === gridBBox.rangeY) {
        return { effectiveResX: gridResolution, effectiveResY: gridResolution };
      }
      const longer = Math.max(gridBBox.rangeX, gridBBox.rangeY);
      const ratioX = gridBBox.rangeX / longer;
      const ratioY = gridBBox.rangeY / longer;
      return {
        effectiveResX: Math.max(2, Math.round(gridResolution * ratioX)),
        effectiveResY: Math.max(2, Math.round(gridResolution * ratioY)),
      };
    }
    // 'step' — krok w metrach, oddzielnie dla X i Y
    return {
      effectiveResX: Math.max(2, Math.round(gridBBox.rangeX / gridStep) + 1),
      effectiveResY: Math.max(2, Math.round(gridBBox.rangeY / gridStep) + 1),
    };
  }, [gridSizeMode, gridResolution, gridStep, gridShapeMode, gridBBox]);

  // Cięcie poziome — zakres Z (min strop skały, max rzędna terenu) z aktualnego gridu
  const sliceZRange = useMemo(() => {
    if (!gridData?.elevationSurface || !gridData?.skalaSurface) return null;
    let zMax = -Infinity, zMin = Infinity;
    for (const row of gridData.elevationSurface) {
      for (const v of row) {
        if (v !== null && v !== undefined && v > zMax) zMax = v;
      }
    }
    for (const row of gridData.skalaSurface) {
      for (const v of row) {
        if (v !== null && v !== undefined && v < zMin) zMin = v;
      }
    }
    if (!Number.isFinite(zMin) || !Number.isFinite(zMax)) return null;
    return { zMin: Math.floor(zMin), zMax: Math.ceil(zMax) };
  }, [gridData]);

  // Macierz klasyfikacji dla cięcia poziomego (Plotly heatmap z = 1|2|3|null)
  // sliceUpsample>1 → bilinear resample surfaces N× przed klasyfikacją (gładsze granice, te same 3 kolory)
  const sliceMatrix = useMemo(() => {
    if (!sliceOpen || !gridData || sliceZ === null) return null;
    const { elevationSurface, zwietrzelinaSurface, skalaSurface, gruntPresenceSurface, zwietPresenceSurface, xCoords, yCoords } = gridData;
    const origRows = yCoords.length;
    const origCols = xCoords.length;
    const factor = Math.max(1, sliceUpsample | 0);
    const rows = factor === 1 ? origRows : (origRows - 1) * factor + 1;
    const cols = factor === 1 ? origCols : (origCols - 1) * factor + 1;

    let outX, outY;
    if (factor === 1) {
      outX = xCoords;
      outY = yCoords;
    } else {
      outX = new Array(cols);
      outY = new Array(rows);
      for (let i = 0; i < cols; i++) outX[i] = xCoords[0] + (xCoords[origCols - 1] - xCoords[0]) * (i / (cols - 1));
      for (let j = 0; j < rows; j++) outY[j] = yCoords[0] + (yCoords[origRows - 1] - yCoords[0]) * (j / (rows - 1));
    }

    // Bilinear sample 2D surface w punkcie (px,py). Null gdy któraś z 4 sąsiednich komórek jest null
    // (poza maską) — żeby maska nie "wyciekała" do wnętrza po wygładzaniu.
    const minX = xCoords[0], maxX = xCoords[origCols - 1];
    const minY = yCoords[0], maxY = yCoords[origRows - 1];
    const stepX = (maxX - minX) / (origCols - 1);
    const stepY = (maxY - minY) / (origRows - 1);
    const sample = (surface, px, py, fallback = null) => {
      if (!surface) return fallback;
      if (px < minX || px > maxX || py < minY || py > maxY) return null;
      const fx = (px - minX) / stepX;
      const fy = (py - minY) / stepY;
      const i0 = Math.floor(fx), j0 = Math.floor(fy);
      const i1 = Math.min(i0 + 1, origCols - 1);
      const j1 = Math.min(j0 + 1, origRows - 1);
      const v00 = surface[j0][i0], v01 = surface[j0][i1];
      const v10 = surface[j1][i0], v11 = surface[j1][i1];
      if (v00 === null || v01 === null || v10 === null || v11 === null) return null;
      const tx = fx - i0, ty = fy - j0;
      return v00 * (1 - tx) * (1 - ty) + v01 * tx * (1 - ty) + v10 * (1 - tx) * ty + v11 * tx * ty;
    };

    const matrix = new Array(rows);
    const labels = new Array(rows);
    const counts = { grunt: 0, zwietrzelina: 0, skala: 0, powietrze: 0 };
    for (let j = 0; j < rows; j++) {
      const row = new Array(cols);
      const labelRow = new Array(cols);
      const py = outY[j];
      for (let i = 0; i < cols; i++) {
        const px = outX[i];
        const elev = factor === 1 ? elevationSurface[j][i] : sample(elevationSurface, px, py);
        const zwietTop = factor === 1 ? zwietrzelinaSurface[j][i] : sample(zwietrzelinaSurface, px, py);
        const skalaTop = factor === 1 ? skalaSurface[j][i] : sample(skalaSurface, px, py);
        const gPres = factor === 1
          ? (gruntPresenceSurface ? gruntPresenceSurface[j][i] : 1)
          : (sample(gruntPresenceSurface, px, py, 1) ?? 1);
        const zPres = factor === 1
          ? (zwietPresenceSurface ? zwietPresenceSurface[j][i] : 1)
          : (sample(zwietPresenceSurface, px, py, 1) ?? 1);
        const cls = classifyAtElevation(sliceZ, elev, zwietTop, skalaTop, gPres, zPres);
        row[i] = cls;
        if (cls === 1) { counts.grunt++; labelRow[i] = 'Grunt'; }
        else if (cls === 2) { counts.zwietrzelina++; labelRow[i] = 'Zwietrzlina'; }
        else if (cls === 3) { counts.skala++; labelRow[i] = 'Skała'; }
        else if (elev !== null && elev !== undefined) { counts.powietrze++; labelRow[i] = 'Powietrze'; }
        else labelRow[i] = 'Poza obszarem';
      }
      matrix[j] = row;
      labels[j] = labelRow;
    }
    return { matrix, labels, counts, xCoords: outX, yCoords: outY };
  }, [sliceOpen, gridData, sliceZ, sliceUpsample]);

  // Inicjalizacja sliceZ po wygenerowaniu gridu — środek zakresu między terenem a skałą
  useEffect(() => {
    if (sliceZRange && sliceZ === null) {
      setSliceZ(Math.round((sliceZRange.zMin + sliceZRange.zMax) / 2));
    } else if (!sliceZRange && sliceZ !== null) {
      setSliceZ(null);
    }
  }, [sliceZRange, sliceZ]);

  // Generuj grid po wczytaniu danych otworów
  const handleGenerateGrid = useCallback(() => {
    if (!boreholeData || boreholeData.length < 3) return;
    // Zapisz kamerę przed przeliczeniem (komponent zniknie z DOM)
    const plotEl = gridPlotRef.current?.el;
    if (plotEl && plotEl.layout && plotEl.layout.scene && plotEl.layout.scene.camera) {
      savedCameraRef.current = JSON.parse(JSON.stringify(plotEl.layout.scene.camera));
    }
    setGeneratingGrid(true);
    setNmtTerrainError(null);
    // Defer to next tick so UI updates
    setTimeout(async () => {
      try {
        const gridOpts = {
          shapeMode: gridShapeMode,
          bufferMeters: effectiveBuffer,
          resolutionY: effectiveResY,
          maxDistanceFromBorehole: gridMaskDistance,
          topK: gridTopK,
        };

        // Jeśli użytkownik chce teren z NMT — znajdź tile pokrywający bbox gridu i bilinearnie próbkuj
        let terrainGrid = null;
        let terrainOffsetPoints = null;
        let terrainSourceInfo = { source: 'idw' };
        if (useNMTTerrain) {
          const coords = computeGridCoordinates(boreholeData, effectiveResX, gridOpts);
          if (!coords) throw new Error('Nie udało się obliczyć siatki gridu');
          const { xCoords, yCoords, bbox } = coords;

          const tileMeta = findCoveringTile(tiles, bbox);
          if (!tileMeta) {
            const fmt = (v) => Math.round(v);
            const gridDesc = `grid: X[${fmt(bbox.minX)}..${fmt(bbox.maxX)}] Y[${fmt(bbox.minY)}..${fmt(bbox.maxY)}] (${fmt(bbox.maxX - bbox.minX)}×${fmt(bbox.maxY - bbox.minY)} m)`;
            const tilesDesc = tiles.length
              ? tiles.map(t => `  tile ${t.fileName}: X[${fmt(t.bbox.minX)}..${fmt(t.bbox.maxX)}] Y[${fmt(t.bbox.minY)}..${fmt(t.bbox.maxY)}] (${fmt(t.bbox.maxX - t.bbox.minX)}×${fmt(t.bbox.maxY - t.bbox.minY)} m)`).join('\n')
              : '  (brak tile w cache)';
            console.error('[NMT] Brak pokrycia:\n' + gridDesc + '\nDostępne tile:\n' + tilesDesc);
            throw new Error(`Brak tile NMT pokrywającego obszar gridu.\n\n${gridDesc}\n\nDostępne tile:\n${tilesDesc}\n\nGrid jest WIĘKSZY niż każdy tile w którymś wymiarze — pobierz większy obszar NMT (kwadrat z marginesem).`);
          }

          // Pobierz pełne dane tile (cache w pamięci? — na razie zawsze z Firebase, są małe)
          const tile = await getTile(selectedProject.id, tileMeta.fileName);
          terrainSourceInfo = { source: 'tile', tileStep: tile.step, tileFileName: tileMeta.fileName };

          // Bilinearne próbkowanie tile na punkty gridu
          const grid = [];
          for (let j = 0; j < yCoords.length; j++) {
            const row = new Array(xCoords.length);
            for (let i = 0; i < xCoords.length; i++) {
              row[i] = bilinearSampleTile(tile, xCoords[i], yCoords[j]);
            }
            grid.push(row);
          }
          terrainGrid = grid;

          // Offset per otwór = geodezja - NMT w lokalizacji otworu → korekta wymusza przejście terenu przez otwory
          terrainOffsetPoints = boreholeData
            .map(b => {
              const nmtAtBh = bilinearSampleTile(tile, b.x, b.y);
              if (nmtAtBh === null) return null; // otwór poza tile — nie korygujemy z niego
              return { x: b.x, y: b.y, value: (b.elevation ?? 0) - nmtAtBh };
            })
            .filter(Boolean);
        }

        const result = await generateInterpolationGrid(boreholeData, effectiveResX, 2, {
          ...gridOpts,
          terrainGrid,
          terrainOffsetPoints,
        });
        setGridData(result);
        setGridTerrainSource(terrainSourceInfo);
      } catch (err) {
        console.error('Blad generowania gridu:', err);
        setNmtTerrainError(err.message || 'Nie udało się wygenerować gridu');
      } finally {
        setGeneratingGrid(false);
      }
    }, 50);
  }, [boreholeData, effectiveResX, effectiveResY, gridShapeMode, effectiveBuffer, gridMaskDistance, gridTopK, useNMTTerrain, selectedProject?.id, tiles]);

  // CSV export
  const downloadGridCSV = useCallback((surfaceName) => {
    if (!gridData) return;

    const { xCoords, yCoords } = gridData;
    let surface;
    let filename;
    if (surfaceName === 'grunt') {
      surface = gridData.gruntSurface;
      filename = 'grid_strop_gruntu.csv';
    } else if (surfaceName === 'zwietrzelina') {
      surface = gridData.zwietrzelinaSurface;
      filename = 'grid_strop_zwietrzliny.csv';
    } else if (surfaceName === 'woda') {
      surface = gridData.waterSurface;
      filename = 'grid_zwierciadlo_wody.csv';
    } else {
      surface = gridData.skalaSurface;
      filename = 'grid_strop_skaly.csv';
    }
    if (!surface) return;

    const rows = ['X,Y,Z'];
    for (let j = 0; j < yCoords.length; j++) {
      for (let i = 0; i < xCoords.length; i++) {
        const val = surface[j][i];
        if (val === null || val === undefined) continue; // pomiń komórki zamaskowane
        rows.push(`${xCoords[i]},${yCoords[j]},${val}`);
      }
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [gridData]);

  const downloadAllGridCSV = useCallback(() => {
    if (!gridData) return;
    const { xCoords, yCoords, gruntSurface, zwietrzelinaSurface, skalaSurface, waterSurface } = gridData;
    const hasWater = !!waterSurface;
    const header = hasWater
      ? 'X,Y,Strop_Gruntu,Strop_Zwietrzliny,Strop_Skaly,Zwierciadlo_Wody'
      : 'X,Y,Strop_Gruntu,Strop_Zwietrzliny,Strop_Skaly';
    const rows = [header];
    for (let j = 0; j < yCoords.length; j++) {
      for (let i = 0; i < xCoords.length; i++) {
        const g = gruntSurface[j][i];
        // Pomiń komórki zamaskowane — wszystkie powierzchnie tam są null
        if (g === null || g === undefined) continue;
        const base = `${xCoords[i]},${yCoords[j]},${g},${zwietrzelinaSurface[j][i]},${skalaSurface[j][i]}`;
        if (hasWater) {
          const w = waterSurface[j][i];
          rows.push(`${base},${w === null || w === undefined ? '' : w}`);
        } else {
          rows.push(base);
        }
      }
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'grid_wszystkie_powierzchnie.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [gridData]);

  // ---- Eksport STL ----
  const buildClosedLayerSTL = useCallback((xCoords, yCoords, topSurface, botSurface, headerText) => {
    const nyG = yCoords.length;
    const nxG = xCoords.length;
    const triangles = [];

    const addTri = (v1, v2, v3) => {
      const ux = v2[0]-v1[0], uy = v2[1]-v1[1], uz = v2[2]-v1[2];
      const wx = v3[0]-v1[0], wy = v3[1]-v1[1], wz = v3[2]-v1[2];
      const nnx = uy*wz - uz*wy;
      const nny = uz*wx - ux*wz;
      const nnz = ux*wy - uy*wx;
      const len = Math.sqrt(nnx*nnx + nny*nny + nnz*nnz) || 1;
      triangles.push({ n: [nnx/len, nny/len, nnz/len], v: [v1, v2, v3] });
    };

    const tV = (j, i) => [xCoords[i], yCoords[j], topSurface[j][i]];
    const bV = (j, i) => [xCoords[i], yCoords[j], botSurface[j][i]];

    // Góra
    for (let j = 0; j < nyG - 1; j++)
      for (let i = 0; i < nxG - 1; i++) {
        addTri(tV(j,i), tV(j+1,i), tV(j+1,i+1));
        addTri(tV(j,i), tV(j+1,i+1), tV(j,i+1));
      }
    // Dół (odwrócony winding)
    for (let j = 0; j < nyG - 1; j++)
      for (let i = 0; i < nxG - 1; i++) {
        addTri(bV(j,i), bV(j+1,i+1), bV(j+1,i));
        addTri(bV(j,i), bV(j,i+1), bV(j+1,i+1));
      }
    // Bok j=0
    for (let i = 0; i < nxG - 1; i++) {
      addTri(tV(0,i), bV(0,i), bV(0,i+1));
      addTri(tV(0,i), bV(0,i+1), tV(0,i+1));
    }
    // Bok j=max
    for (let i = 0; i < nxG - 1; i++) {
      addTri(tV(nyG-1,i), bV(nyG-1,i+1), bV(nyG-1,i));
      addTri(tV(nyG-1,i), tV(nyG-1,i+1), bV(nyG-1,i+1));
    }
    // Bok i=0
    for (let j = 0; j < nyG - 1; j++) {
      addTri(tV(j,0), bV(j+1,0), bV(j,0));
      addTri(tV(j,0), tV(j+1,0), bV(j+1,0));
    }
    // Bok i=max
    for (let j = 0; j < nyG - 1; j++) {
      addTri(tV(j,nxG-1), bV(j,nxG-1), bV(j+1,nxG-1));
      addTri(tV(j,nxG-1), bV(j+1,nxG-1), tV(j+1,nxG-1));
    }

    // Binary STL
    const numTri = triangles.length;
    const buf = new ArrayBuffer(84 + numTri * 50);
    const view = new DataView(buf);
    for (let i = 0; i < 80; i++)
      view.setUint8(i, i < headerText.length ? headerText.charCodeAt(i) : 0);
    view.setUint32(80, numTri, true);
    let off = 84;
    for (const tri of triangles) {
      view.setFloat32(off, tri.n[0], true); off += 4;
      view.setFloat32(off, tri.n[1], true); off += 4;
      view.setFloat32(off, tri.n[2], true); off += 4;
      for (const v of tri.v) {
        view.setFloat32(off, v[0], true); off += 4;
        view.setFloat32(off, v[1], true); off += 4;
        view.setFloat32(off, v[2], true); off += 4;
      }
      view.setUint16(off, 0, true); off += 2;
    }
    return buf;
  }, []);

  const downloadSTL = useCallback(() => {
    if (!gridData) return;
    const { xCoords, yCoords, elevationSurface, skalaSurface } = gridData;
    const buf = buildClosedLayerSTL(xCoords, yCoords, elevationSurface, skalaSurface, 'Model geologiczny - calosc');
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'model_geologiczny_3d.stl';
    a.click();
    URL.revokeObjectURL(url);
  }, [gridData, buildClosedLayerSTL]);

  const downloadLayerSTL = useCallback((layerName) => {
    if (!gridData) return;
    const { xCoords, yCoords, elevationSurface, gruntSurface, zwietrzelinaSurface, skalaSurface } = gridData;
    let topSurf, botSurf, filename, header;
    if (layerName === 'grunt') {
      topSurf = elevationSurface;
      botSurf = gruntSurface;
      filename = 'warstwa_grunt.stl';
      header = 'Grunt - strop terenu do spag gruntu';
    } else if (layerName === 'zwietrzelina') {
      topSurf = gruntSurface;
      botSurf = zwietrzelinaSurface;
      filename = 'warstwa_zwietrzelina.stl';
      header = 'Zwietrzelina - spag gruntu do spag zwietrz';
    } else {
      topSurf = zwietrzelinaSurface;
      botSurf = skalaSurface;
      filename = 'warstwa_skala.stl';
      header = 'Skala - spag zwietrzliny do spag skaly';
    }
    const buf = buildClosedLayerSTL(xCoords, yCoords, topSurf, botSurf, header);
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [gridData, buildClosedLayerSTL]);

  const downloadHTML3D = useCallback(() => {
    const plotEl = gridPlotRef.current?.el;
    if (!plotEl || !plotEl.data || !plotEl.layout) return;

    const data = JSON.stringify(plotEl.data);
    const layout = JSON.stringify({
      ...plotEl.layout,
      // Wymuś sensowny rozmiar
      height: 700,
      width: undefined,
    });

    const html = `<!DOCTYPE html>
<html lang="pl">
<head>
<meta charset="UTF-8">
<title>Model geologiczny 3D</title>
<script src="https://cdn.plot.ly/plotly-2.27.0.min.js"><\/script>
<style>
  body { margin: 0; padding: 0; font-family: Arial, sans-serif; background: #fafafa; }
  #chart { width: 100vw; height: 100vh; }
  .info { position: fixed; bottom: 8px; left: 12px; font-size: 11px; color: #888; z-index: 10; }
</style>
</head>
<body>
<div id="chart"></div>
<div class="info">Model geologiczny 3D &mdash; Obracaj: LPM, Zoom: scroll, Przesu\u0144: PPM</div>
<script>
  var data = ${data};
  var layout = ${layout};
  layout.height = undefined;
  layout.autosize = true;
  Plotly.newPlot('chart', data, layout, {responsive: true, displaylogo: false});
<\/script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'model_geologiczny_3d.html';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // Woksele 3D
  const [voxelData, setVoxelData] = useState(null);
  const [voxelResXY, setVoxelResXY] = useState(25);
  const [voxelResZ, setVoxelResZ] = useState(20);
  const [voxelAspectZ, setVoxelAspectZ] = useState(0.4);
  const [generatingVoxels, setGeneratingVoxels] = useState(false);

  const handleGenerateVoxels = useCallback(() => {
    if (!boreholeData || boreholeData.length < 3) return;
    // Zapisz kamerę przed przeliczeniem (komponent zniknie z DOM)
    const plotEl = voxelPlotRef.current?.el;
    if (plotEl && plotEl.layout && plotEl.layout.scene && plotEl.layout.scene.camera) {
      savedVoxelCameraRef.current = JSON.parse(JSON.stringify(plotEl.layout.scene.camera));
    }
    setGeneratingVoxels(true);
    setTimeout(async () => {
      // Woksele używają tych samych ustawień kształtu co surface grid (spójność wizualna)
      const voxelOpts = {
        shapeMode: gridShapeMode,
        bufferMeters: effectiveBuffer,
      };
      // Dla prostokąta — proporcjonalna rozdzielczość Y
      if (gridShapeMode === 'rect' && gridBBox && gridBBox.rangeX > 0 && gridBBox.rangeY > 0) {
        const ratio = gridBBox.rangeY / gridBBox.rangeX;
        voxelOpts.resY = Math.max(2, Math.round(voxelResXY * ratio));
      }
      const result = await generateVoxelGrid(boreholeData, voxelResXY, voxelResZ, 2, voxelOpts);
      setVoxelData(result);
      setGeneratingVoxels(false);
    }, 50);
  }, [boreholeData, voxelResXY, voxelResZ, gridShapeMode, effectiveBuffer, gridBBox]);

  // Eksport CSV cięcia poziomego — X, Y, Typ (Grunt/Zwietrzlina/Skała/Powietrze) na rzędnej sliceZ.
  // Pomija komórki maski (null poza convex hullem otworów + bufor) — w pliku tylko widoczne komórki.
  const downloadSliceCSV = useCallback(() => {
    if (!sliceMatrix || sliceZ == null) return;
    const { xCoords, yCoords, matrix, labels } = sliceMatrix;
    const rows = [`# Cięcie poziome na Z = ${sliceZ.toFixed(2)} m n.p.m.`, 'X,Y,Z,Typ'];
    for (let j = 0; j < yCoords.length; j++) {
      for (let i = 0; i < xCoords.length; i++) {
        const v = matrix[j][i];
        if (v === null || v === undefined) continue; // maska / poza obszarem
        const label = labels?.[j]?.[i] || (v === 1 ? 'Grunt' : v === 2 ? 'Zwietrzlina' : v === 3 ? 'Skała' : 'Inny');
        rows.push(`${xCoords[i]},${yCoords[j]},${sliceZ.toFixed(2)},${label}`);
      }
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ciecie_poziome_Z${sliceZ.toFixed(1).replace('.', '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sliceMatrix, sliceZ]);

  const downloadVoxelCSV = useCallback(() => {
    if (!voxelData) return;
    const rows = ['X,Y,Z,Typ'];
    for (const v of voxelData.voxels) {
      rows.push(`${v.x},${v.y},${v.z},${v.type}`);
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'voxel_model_3d.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [voxelData]);

  const downloadVoxelHTML3D = useCallback(() => {
    const plotEl = voxelPlotRef.current?.el;
    if (!plotEl || !plotEl.data || !plotEl.layout) return;

    const data = JSON.stringify(plotEl.data);
    const layout = JSON.stringify({ ...plotEl.layout, height: undefined });

    const html = `<!DOCTYPE html>
<html lang="pl">
<head>
<meta charset="UTF-8">
<title>Model wokselowy 3D</title>
<script src="https://cdn.plot.ly/plotly-2.27.0.min.js"><\/script>
<style>
  body { margin: 0; padding: 0; font-family: Arial, sans-serif; background: #fafafa; }
  #chart { width: 100vw; height: 100vh; }
  .info { position: fixed; bottom: 8px; left: 12px; font-size: 11px; color: #888; z-index: 10; }
</style>
</head>
<body>
<div id="chart"></div>
<div class="info">Model wokselowy 3D &mdash; Obracaj: LPM, Zoom: scroll, Przesu\u0144: PPM</div>
<script>
  var data = ${data};
  var layout = ${layout};
  layout.autosize = true;
  Plotly.newPlot('chart', data, layout, {responsive: true, displaylogo: false});
<\/script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'model_wokselowy_3d.html';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // Przygotuj dane Plotly z wokseli (pogrupowane per typ)
  const voxelPlotData = useMemo(() => {
    if (!voxelData) return null;

    const types = {
      grunt: { color: ZONE_COLORS.grunt, label: 'Grunt', voxels: [] },
      zwietrzelina: { color: ZONE_COLORS.zwietrzelina, label: 'Zwietrzlina', voxels: [] },
      skala: { color: ZONE_COLORS.skala, label: 'Skala', voxels: [] },
    };

    for (const v of voxelData.voxels) {
      types[v.type].voxels.push(v);
    }

    return Object.entries(types)
      // Sanitizacja: usuń voxele z NaN/undefined oraz pomiń trace'y bez punktów —
      // gl-plot3d crashuje przy pustych/NaN arrays w scatter3d ("cannot read [0] of undefined").
      .map(([key, { color, label, voxels }]) => {
        const safe = voxels.filter(v => Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z));
        return { key, color, label, voxels: safe };
      })
      .filter(({ voxels }) => voxels.length > 0)
      .map(({ key, color, label, voxels }) => ({
        type: 'scatter3d',
        mode: 'markers',
        x: voxels.map(v => v.x),
        y: voxels.map(v => v.y),
        z: voxels.map(v => v.z),
        name: label,
        marker: {
          size: Math.max(2, Math.min(5, 800 / voxelData.voxels.length * 10)),
          color,
          opacity: key === 'skala' ? 0.4 : key === 'zwietrzelina' ? 0.6 : 0.8,
          symbol: 'square',
        },
        hovertemplate: `<b>${label}</b><br>X: %{x:.1f}<br>Y: %{y:.1f}<br>Z: %{z:.2f} m n.p.m.<extra></extra>`,
      }));
  }, [voxelData]);

  // Przekrój z modelu 3D
  const [crossSectionPointA, setCrossSectionPointA] = useState(null); // {x, y}
  const [crossSectionPointB, setCrossSectionPointB] = useState(null); // {x, y}
  // Liczba punktów kontrolnych interpolacji wzdłuż linii A→B (IDW samplingu).
  // Większa liczba = gładszy przekrój, ale więcej słupków w edytorze.
  const [crossSectionCount, setCrossSectionCount] = useState(20);
  const [crossSectionBoreholes, setCrossSectionBoreholes] = useState(null);
  const [crossSectionMode, setCrossSectionMode] = useState(false); // tryb wybierania punktów
  const gridPlotRef = useRef(null); // ref do wykresu Plotly 3D
  const [gridIsFullscreen, setGridIsFullscreen] = useState(false);

  // ESC zamyka tryb pełnoekranowy
  useEffect(() => {
    if (!gridIsFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setGridIsFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [gridIsFullscreen]);

  const toggleGridFullscreen = useCallback(() => {
    setGridIsFullscreen(v => !v);
  }, []);
  const voxelPlotRef = useRef(null); // ref do wykresu wokselowego Plotly 3D
  const savedCameraRef = useRef(null); // zachowana pozycja kamery 3D (ref, nie state — żeby nie triggerować re-render)
  const savedVoxelCameraRef = useRef(null); // zachowana pozycja kamery wokselowego 3D

  // Odczytaj aktualną kamerę z instancji Plotly (grid 3D)
  const readCurrentCamera = useCallback(() => {
    const plotEl = gridPlotRef.current?.el;
    if (plotEl && plotEl.layout && plotEl.layout.scene && plotEl.layout.scene.camera) {
      savedCameraRef.current = JSON.parse(JSON.stringify(plotEl.layout.scene.camera));
    }
  }, []);

  // Odczytaj aktualną kamerę z instancji Plotly (woksele 3D)
  const readVoxelCamera = useCallback(() => {
    const plotEl = voxelPlotRef.current?.el;
    if (plotEl && plotEl.layout && plotEl.layout.scene && plotEl.layout.scene.camera) {
      savedVoxelCameraRef.current = JSON.parse(JSON.stringify(plotEl.layout.scene.camera));
    }
  }, []);

  const handleCrossSectionGenerate = useCallback(() => {
    if (!crossSectionPointA || !crossSectionPointB || !boreholeData) return;
    const boreholes = generateCrossSectionBoreholes(
      crossSectionPointA, crossSectionPointB, boreholeData, crossSectionCount, 2
    );
    setCrossSectionBoreholes(boreholes);
  }, [crossSectionPointA, crossSectionPointB, boreholeData, crossSectionCount]);

  const handleOpenInEditor = useCallback(() => {
    if (!crossSectionBoreholes || crossSectionBoreholes.length === 0) return;
    navigate('/editor', { state: { importedBoreholes: crossSectionBoreholes, autoInterpolate: true } });
  }, [crossSectionBoreholes, navigate]);

  // Kliknięcie w wykres Plotly surface grid — wybierz punkt A lub B
  const handlePlotClick = useCallback((eventData) => {
    if (!crossSectionMode || !eventData || !eventData.points || eventData.points.length === 0) return;
    const pt = eventData.points[0];
    if (pt.x == null || pt.y == null) return;
    const clickedPoint = { x: pt.x, y: pt.y };

    // Zapisz kamerę przed zmianą state (re-render)
    readCurrentCamera();

    if (!crossSectionPointA) {
      setCrossSectionPointA(clickedPoint);
    } else if (!crossSectionPointB) {
      setCrossSectionPointB(clickedPoint);
    } else {
      // Reset — nowy punkt A
      setCrossSectionPointA(clickedPoint);
      setCrossSectionPointB(null);
      setCrossSectionBoreholes(null);
    }
  }, [crossSectionMode, crossSectionPointA, crossSectionPointB, readCurrentCamera]);

  useEffect(() => {
    if (!projectsLoaded) {
      loadProjects();
    }
  }, [projectsLoaded, loadProjects]);

  // Wykrywanie strefy PL-2000 z otworów projektu (działa zanim user wczyta pełne warstwy)
  const detectZone = useCallback(() => {
    if (boreholeData && boreholeData.length > 0) return Math.floor(boreholeData[0].y / 1000000);
    if (allBoreholes && allBoreholes.length > 0) {
      const first = allBoreholes.find(b => b.coordY);
      if (first) return Math.floor(first.coordY / 1000000);
    }
    return null;
  }, [boreholeData, allBoreholes]);

  // Listowanie tile NMT po zmianie projektu
  const refreshTiles = useCallback(async () => {
    if (!selectedProject?.id) {
      setTiles([]);
      return;
    }
    setTilesLoading(true);
    try {
      const list = await listTiles(selectedProject.id);
      setTiles(list);
    } catch (err) {
      console.warn('Listowanie tile NMT błąd:', err);
      setTiles([]);
    } finally {
      setTilesLoading(false);
    }
  }, [selectedProject?.id]);

  useEffect(() => { refreshTiles(); }, [refreshTiles]);

  // Pobieranie tile NMT — deleguje do globalnego context'u (banner zostaje widoczny przy nawigacji)
  const { startTileDownload, download: nmtDownload } = useNmtDownload();
  const tileFetching = nmtDownload && (nmtDownload.status === 'fetching' || nmtDownload.status === 'saving');

  const handleFetchTile = useCallback(async () => {
    if (!selectedProject?.id || !tileDrawBbox) return;
    setTileFetchError(null);
    const { minX, maxX, minY, maxY } = tileDrawBbox;
    const step = tileDrawStep;
    const resX = Math.max(2, Math.round((maxX - minX) / step) + 1);
    const resY = Math.max(2, Math.round((maxY - minY) / step) + 1);
    const stepX = (maxX - minX) / (resX - 1);
    const stepY = (maxY - minY) / (resY - 1);
    const xCoords = Array.from({ length: resX }, (_, i) => minX + i * stepX);
    const yCoords = Array.from({ length: resY }, (_, j) => minY + j * stepY);
    try {
      await startTileDownload({
        projectId: selectedProject.id,
        bbox: tileDrawBbox,
        step,
        xCoords,
        yCoords,
        onSuccess: () => {
          refreshTiles();
          setTileDrawBbox(null);
        },
        onError: (err) => setTileFetchError(err.message || 'Nie udało się pobrać NMT'),
      });
    } catch (err) {
      setTileFetchError(err.message || 'Nie udało się pobrać NMT');
    }
  }, [selectedProject?.id, tileDrawBbox, tileDrawStep, refreshTiles, startTileDownload]);

  const handleDeleteTile = useCallback(async (fileName) => {
    if (!selectedProject?.id) return;
    if (!window.confirm(`Usunąć tile ${fileName}?`)) return;
    try {
      await deleteTile(selectedProject.id, fileName);
      await refreshTiles();
    } catch (err) {
      console.error('Usuwanie tile NMT błąd:', err);
      alert('Nie udało się usunąć tile: ' + err.message);
    }
  }, [selectedProject?.id, refreshTiles]);

  // Po wybraniu projektu — automatycznie pobierz warstwy i sprawdź strefy dla WSZYSTKICH otworów
  const [zoneInfoLoading, setZoneInfoLoading] = useState(false);
  useEffect(() => {
    if (!selectedProject || allBoreholes.length === 0) {
      setBoreholeZoneInfo({});
      return;
    }

    let cancelled = false;
    const fetchAllZones = async () => {
      setZoneInfoLoading(true);
      try {
        const results = await Promise.allSettled(
          allBoreholes.map(b => fetchBoreholeWithLayers(selectedProject.id, b.id, layerColors))
        );
        if (cancelled) return;

        const zoneInfo = {};
        results.forEach((result, index) => {
          const id = allBoreholes[index].id;
          if (result.status === 'rejected' || !result.value) {
            zoneInfo[id] = {
              hasGrunt: false,
              hasZwietrzelina: false,
              hasSkala: false,
              hasWater: false,
              waterZust: null,
              allThree: false,
              zones: [],
              error: true,
            };
            return;
          }
          const bh = result.value;
          if (!bh.layers || bh.layers.length === 0) {
            zoneInfo[id] = { hasGrunt: false, hasZwietrzelina: false, hasSkala: false, hasWater: false, waterZust: null, allThree: false, zones: [], error: true };
            return;
          }
          const boundaries = extractZoneBoundaries(bh.layers);
          const hasGrunt = boundaries.gruntBottom > 0;
          const hasZwietrzelina = boundaries.zwietrzelinaBottom > boundaries.gruntBottom;
          const hasSkala = boundaries.totalDepth > boundaries.zwietrzelinaBottom;
          const bhName = allBoreholes[index]?.nameBorehole || id;
          console.log(`[zoneInfo] ${bhName}:`, { ...boundaries, hasGrunt, hasZwietrzelina, hasSkala });
          // Woda: zwierciadło ustabilizowane, a jak brak to najwyższe sączenie
          const stabilizedWaters = (bh.waterLevels || []).filter(w => w.type === 'stabilized-drilled' && w.depthFrom != null);
          const seepageWaters = (bh.waterLevels || []).filter(w => w.type === 'seepage' && w.depth != null);
          const hasStabilized = stabilizedWaters.length > 0;
          const hasSeepage = seepageWaters.length > 0;
          const hasWater = hasStabilized || hasSeepage;
          const waterZust = hasStabilized ? Math.min(...stabilizedWaters.map(w => w.depthFrom))
            : hasSeepage ? Math.min(...seepageWaters.map(w => w.depth))
            : null;

          zoneInfo[id] = {
            hasGrunt,
            hasZwietrzelina,
            hasSkala,
            hasWater,
            waterZust,
            allThree: hasGrunt && hasZwietrzelina && hasSkala,
            zones: [hasGrunt && 'grunt', hasZwietrzelina && 'zwietrzelina', hasSkala && 'skala'].filter(Boolean),
            gruntBottom: boundaries.gruntBottom,
            zwietrzelinaBottom: boundaries.zwietrzelinaBottom,
          };
        });
        setBoreholeZoneInfo(zoneInfo);
      } catch (err) {
        console.error('Blad pobierania stref:', err);
      } finally {
        if (!cancelled) setZoneInfoLoading(false);
      }
    };

    fetchAllZones();
    return () => { cancelled = true; };
  }, [selectedProject, allBoreholes, layerColors]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Blad wylogowania:', err);
    }
  };

  // Pobierz pełne dane warstw dla wybranych otworów
  const handleLoadData = useCallback(async () => {
    if (!selectedProject || orderedSelection.length < 3) return;

    setLoadingData(true);
    setDataError(null);
    setVirtualBorehole(null);
    setClickCoords(null);

    try {
      const results = await Promise.all(
        orderedSelection.map(sel => fetchBoreholeWithLayers(selectedProject.id, sel.id, layerColors))
      );

      // Dołącz coordX/coordY z listy otworów
      const boreholeMap = {};
      allBoreholes.forEach(b => { boreholeMap[b.id] = b; });

      const fullBoreholes = results.map((bh, index) => {
        const fb = boreholeMap[orderedSelection[index].id];
        return {
          ...bh,
          coordX: fb?.coordX || null,
          coordY: fb?.coordY || null,
          coordZ: fb?.coordZ || null,
          depth: fb?.coordZ || bh.depth || 0,
        };
      });

      console.log('[handleLoadData] fullBoreholes coords:', fullBoreholes.map(b => ({ name: b.name, coordX: b.coordX, coordY: b.coordY })));
      const data = prepareBoreholeData(fullBoreholes);
      console.log('[handleLoadData] preparedData:', data.map(b => ({ name: b.name, x: b.x, y: b.y })));
      if (data.length < 3) {
        setDataError('Potrzeba min. 3 otworów ze współrzędnymi i warstwami.');
        return;
      }
      setBoreholeData(data);
    } catch (err) {
      console.error('Blad ladowania danych:', err);
      setDataError('Nie udało się pobrać danych warstw.');
    } finally {
      setLoadingData(false);
    }
  }, [selectedProject, orderedSelection, allBoreholes, layerColors]);

  const selectedCount = selectedBoreholeIds.size;

  const boreholeNameMap = useMemo(() => {
    const map = {};
    allBoreholes.forEach(b => { map[b.id] = b.nameBorehole || '(brak nazwy)'; });
    return map;
  }, [allBoreholes]);

  // Mapa Leaflet
  const mapPoints = useMemo(() => {
    const withCoords = allBoreholes.filter(b => b.coordX != null && b.coordY != null);
    if (withCoords.length === 0) return null;

    const allPts = withCoords.map(b => {
      const { lat, lng } = pl2000ToWGS84(b.coordX, b.coordY);
      return { id: b.id, name: b.nameBorehole || '(brak nazwy)', lat, lng, coordX: b.coordX, coordY: b.coordY };
    });

    // Filtruj outlierów — otwory oddalone >5km od mediany
    if (allPts.length >= 3) {
      const lats = allPts.map(p => p.lat).sort((a, b) => a - b);
      const lngs = allPts.map(p => p.lng).sort((a, b) => a - b);
      const medLat = lats[Math.floor(lats.length / 2)];
      const medLng = lngs[Math.floor(lngs.length / 2)];
      const MAX_DIST_KM = 5;
      const filtered = allPts.filter(p => {
        const dLat = (p.lat - medLat) * 111;
        const dLng = (p.lng - medLng) * 70;
        return Math.sqrt(dLat * dLat + dLng * dLng) <= MAX_DIST_KM;
      });
      if (filtered.length > 0) return filtered;
    }

    return allPts;
  }, [allBoreholes]);

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const clusterGroupRef = useRef(null);
  const virtualMarkerRef = useRef(null);
  const toggleSelectionRef = useRef(toggleBoreholeSelection);
  toggleSelectionRef.current = toggleBoreholeSelection;
  const addBoreholesByIdsRef = useRef(addBoreholesByIds);
  addBoreholesByIdsRef.current = addBoreholesByIds;
  const selectionRectRef = useRef(null); // prostokąt zaznaczania na mapie
  const mapPointsRef = useRef(null);
  mapPointsRef.current = mapPoints;
  const boreholeDataRef = useRef(null);
  boreholeDataRef.current = boreholeData;

  // Callback do obsługi kliknięcia w mapę (interpolacja)
  const handleMapClickRef = useRef(null);
  handleMapClickRef.current = useCallback((lat, lng) => {
    if (!boreholeDataRef.current) return;

    // Wykryj strefę z coordY otworów źródłowych (prefiks strefy = pierwsza cyfra coordY)
    const data = boreholeDataRef.current;
    const zone = Math.floor(data[0].y / 1000000);
    const { coordX, coordY } = wgs84ToPl2000(lat, lng, zone);
    const result = interpolateVirtualBorehole(coordX, coordY, data);

    // Otwórz popup SYNCHRONICZNIE w handlerze (zachowuje user gesture → omija popup blocker).
    if (!popupBlockedRef.current) {
      const w = popupWindowRef.current;
      if (!w || w.closed) {
        const newW = window.open('', '_blank', 'width=720,height=640,menubar=no,toolbar=no,location=no,status=no');
        if (newW) {
          popupWindowRef.current = newW;
          setPopupOpen(true);
        } else {
          popupBlockedRef.current = true;
          setPopupBlocked(true);
        }
      } else {
        try { w.focus(); } catch (_) {}
        setPopupOpen(true);
      }
    }

    setVirtualBorehole(result);
    setClickCoords({ lat, lng, coordX: Math.round(coordX * 100) / 100, coordY: Math.round(coordY * 100) / 100 });
  }, []);

  // Ref dla aktualnego trybu rysowania obszaru NMT (handlery Leaflet są podpięte raz)
  const tileDrawModeRef = useRef(false);
  tileDrawModeRef.current = tileDrawMode;
  const onTileBboxDrawnRef = useRef(null);
  onTileBboxDrawnRef.current = (latlngs) => {
    const zone = detectZone();
    if (zone === null) {
      setTileFetchError('Brak otworów ze współrzędnymi w projekcie — wybierz projekt z otworami PL-2000');
      return;
    }
    const a = wgs84ToPl2000(latlngs.start.lat, latlngs.start.lng, zone);
    const b = wgs84ToPl2000(latlngs.end.lat, latlngs.end.lng, zone);
    setTileDrawBbox({
      minX: Math.min(a.coordX, b.coordX),
      maxX: Math.max(a.coordX, b.coordX),
      minY: Math.min(a.coordY, b.coordY),
      maxY: Math.max(a.coordY, b.coordY),
    });
    setTileFetchError(null);
  };

  // Efekt 1: Tworzenie mapy i markerów
  useEffect(() => {
    if (!mapPoints || mapPoints.length === 0) {
      if (clusterGroupRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(clusterGroupRef.current);
        clusterGroupRef.current = null;
      }
      markersRef.current = {};
      return;
    }

    const L = window.L;
    if (!L || !mapContainerRef.current) return;

    // Tworzenie mapy (raz) — kontener jest zawsze w DOM
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapContainerRef.current, {
        scrollWheelZoom: true,
        zoomControl: true,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
      }).addTo(mapInstanceRef.current);

      // Kliknięcie w mapę → interpolacja wirtualnego otworu
      mapInstanceRef.current.on('click', (e) => {
        if (handleMapClickRef.current) {
          handleMapClickRef.current(e.latlng.lat, e.latlng.lng);
        }
      });

      // --- Zaznaczanie prostokątne ---
      // Tryb 1: Shift + drag → zaznaczenie otworów (fioletowy)
      // Tryb 2: tileDrawModeRef.current === true → drag rysuje obszar NMT (różowy)
      let rectStart = null;
      let rectLayer = null;
      let rectMode = null; // 'select' | 'tile'

      mapInstanceRef.current.on('mousedown', (e) => {
        const isTileMode = tileDrawModeRef.current === true;
        const isSelectMode = e.originalEvent.shiftKey;
        if (!isTileMode && !isSelectMode) return;
        rectMode = isTileMode ? 'tile' : 'select';
        mapInstanceRef.current.dragging.disable();
        rectStart = e.latlng;
        if (rectLayer) { mapInstanceRef.current.removeLayer(rectLayer); rectLayer = null; }
      });

      mapInstanceRef.current.on('mousemove', (e) => {
        if (!rectStart) return;
        const bounds = L.latLngBounds(rectStart, e.latlng);
        const color = rectMode === 'tile' ? '#ec4899' : '#7c3aed'; // różowy dla tile, fioletowy dla selekcji
        if (rectLayer) { rectLayer.setBounds(bounds); }
        else {
          rectLayer = L.rectangle(bounds, {
            color, weight: 2, fillColor: color, fillOpacity: 0.15,
            dashArray: '6 4', interactive: false,
          }).addTo(mapInstanceRef.current);
        }
      });

      mapInstanceRef.current.on('mouseup', (e) => {
        if (!rectStart) return;
        const bounds = L.latLngBounds(rectStart, e.latlng);
        const mode = rectMode;
        const startLatLng = rectStart;
        rectStart = null;
        rectMode = null;
        mapInstanceRef.current.dragging.enable();
        if (rectLayer) { mapInstanceRef.current.removeLayer(rectLayer); rectLayer = null; }

        if (mode === 'tile') {
          // Obszar NMT — przekaż do callbacku
          if (onTileBboxDrawnRef.current) {
            onTileBboxDrawnRef.current({ start: startLatLng, end: e.latlng });
          }
          return;
        }

        // Tryb 'select' — znajdź otwory wewnątrz prostokąta
        const pts = mapPointsRef.current;
        if (!pts) return;
        const ids = pts.filter(pt => bounds.contains(L.latLng(pt.lat, pt.lng))).map(pt => pt.id);
        if (ids.length > 0 && addBoreholesByIdsRef.current) {
          addBoreholesByIdsRef.current(ids);
        }
      });
    }

    const map = mapInstanceRef.current;

    // invalidateSize — kontener przechodzi z height:0 na height:500
    const t1 = setTimeout(() => { if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize(); }, 50);
    const t2 = setTimeout(() => { if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize(); }, 300);
    const cleanup = () => { clearTimeout(t1); clearTimeout(t2); };

    // Usuń starą cluster group
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }
    markersRef.current = {};

    // Clustering przy >50 otworach
    const useClustering = mapPoints.length > 50 && L.markerClusterGroup;
    const clusterGroup = useClustering
      ? L.markerClusterGroup({ maxClusterRadius: 40, spiderfyOnMaxZoom: true, showCoverageOnHover: false, disableClusteringAtZoom: 16 })
      : L.layerGroup();
    clusterGroupRef.current = clusterGroup;

    const permanentTooltips = mapPoints.length <= 80;

    const bounds = [];
    mapPoints.forEach(pt => {
      const icon = L.divIcon({
        className: '',
        html: `<div style="
          width:12px; height:12px;
          background:#3b82f6;
          border:2px solid #2563eb;
          border-radius:50%; opacity:0.8;
          transform:translate(-50%,-50%);
        "></div>`,
        iconSize: [0, 0],
      });

      const marker = L.marker([pt.lat, pt.lng], { icon })
        .bindTooltip(pt.name, { permanent: permanentTooltips, direction: 'right', offset: [8, 0], className: 'borehole-label' });

      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        toggleSelectionRef.current(pt.id);
      });

      clusterGroup.addLayer(marker);
      markersRef.current[pt.id] = marker;
      bounds.push([pt.lat, pt.lng]);
    });

    map.addLayer(clusterGroup);

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
    }

    return cleanup;
  }, [mapPoints]);

  // Efekt 2: Aktualizacja ikon zaznaczonych otworów
  useEffect(() => {
    const L = window.L;
    const pts = mapPointsRef.current;
    if (!L || !mapInstanceRef.current || !pts) return;

    pts.forEach(pt => {
      const marker = markersRef.current[pt.id];
      if (!marker) return;
      const isSelected = selectedBoreholeIds.has(pt.id);
      try {
        const icon = L.divIcon({
          className: '',
          html: `<div style="
            width:${isSelected ? 16 : 12}px; height:${isSelected ? 16 : 12}px;
            background:${isSelected ? '#16a34a' : '#3b82f6'};
            border:2px solid ${isSelected ? '#15803d' : '#2563eb'};
            border-radius:50%; opacity:${isSelected ? 1 : 0.8};
            transform:translate(-50%,-50%);
          "></div>`,
          iconSize: [0, 0],
        });
        marker.setIcon(icon);
      } catch { /* marker may not be on map yet */ }
    });

    // Odśwież cluster group po zmianie ikon
    if (clusterGroupRef.current && clusterGroupRef.current.refreshClusters) {
      clusterGroupRef.current.refreshClusters();
    }
  }, [selectedBoreholeIds]);

  // Efekt: rysuje istniejące tile NMT + zatwierdzony prostokąt obszaru NMT na mapie
  const tileLayersRef = useRef({}); // { fileName: L.Rectangle }
  const drawnBboxLayerRef = useRef(null);

  useEffect(() => {
    const L = window.L;
    const map = mapInstanceRef.current;
    const zone = detectZone();
    if (!L || !map || zone === null) return;

    const bboxToBounds = (bbox) => {
      const sw = pl2000ToWGS84(bbox.minX, bbox.minY, zone);
      const ne = pl2000ToWGS84(bbox.maxX, bbox.maxY, zone);
      return L.latLngBounds([sw.lat, sw.lng], [ne.lat, ne.lng]);
    };

    // Wyczyść stare warstwy tile
    Object.values(tileLayersRef.current).forEach(layer => map.removeLayer(layer));
    tileLayersRef.current = {};

    // Dodaj aktualne tile — kolor wg indeksu (zgodny z kropką na liście)
    tiles.forEach((t, idx) => {
      try {
        const bounds = bboxToBounds(t.bbox);
        const color = tileColorAt(idx);
        const layer = L.rectangle(bounds, {
          color, weight: 1.5, fillColor: color, fillOpacity: 0.08,
          interactive: false,
        }).addTo(map);
        tileLayersRef.current[t.fileName] = layer;
      } catch (err) {
        console.warn('Rysowanie tile NMT błąd:', err);
      }
    });

    return () => {
      Object.values(tileLayersRef.current).forEach(layer => map.removeLayer(layer));
      tileLayersRef.current = {};
    };
  }, [tiles, detectZone]);

  // Efekt: rysuje aktualnie zatwierdzony obszar NMT (tileDrawBbox) jako różowy prostokąt
  useEffect(() => {
    const L = window.L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    if (drawnBboxLayerRef.current) {
      map.removeLayer(drawnBboxLayerRef.current);
      drawnBboxLayerRef.current = null;
    }

    const zone = detectZone();
    if (!tileDrawBbox || zone === null) return;
    const sw = pl2000ToWGS84(tileDrawBbox.minX, tileDrawBbox.minY, zone);
    const ne = pl2000ToWGS84(tileDrawBbox.maxX, tileDrawBbox.maxY, zone);
    drawnBboxLayerRef.current = L.rectangle(L.latLngBounds([sw.lat, sw.lng], [ne.lat, ne.lng]), {
      color: '#ec4899', weight: 2, fillColor: '#ec4899', fillOpacity: 0.12,
      dashArray: '6 4', interactive: false,
    }).addTo(map);

    return () => {
      if (drawnBboxLayerRef.current) {
        map.removeLayer(drawnBboxLayerRef.current);
        drawnBboxLayerRef.current = null;
      }
    };
  }, [tileDrawBbox, detectZone]);

  // Efekt: kursor crosshair gdy tryb rysowania obszaru NMT aktywny
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const container = map.getContainer();
    if (tileDrawMode) container.style.cursor = 'crosshair';
    else container.style.cursor = '';
  }, [tileDrawMode]);

  // Efekt 3: marker wirtualnego otworu
  useEffect(() => {
    const L = window.L;
    if (!L || !mapInstanceRef.current) return;

    if (virtualMarkerRef.current) {
      virtualMarkerRef.current.remove();
      virtualMarkerRef.current = null;
    }

    if (clickCoords) {
      const icon = L.divIcon({
        className: '',
        html: `<div style="
          width:20px; height:20px;
          background:#e11d48;
          border:3px solid #be123c;
          border-radius:50%;
          transform:translate(-50%,-50%);
          box-shadow: 0 0 8px rgba(225,29,72,0.5);
        "></div>`,
        iconSize: [0, 0],
      });

      virtualMarkerRef.current = L.marker([clickCoords.lat, clickCoords.lng], { icon })
        .addTo(mapInstanceRef.current)
        .bindTooltip('Wirtualny otwór', { permanent: true, direction: 'right', offset: [12, 0] });
    }
  }, [clickCoords]);

  // Zamknij popup gdy strona jest unmounted (np. nawigacja w SPA).
  useEffect(() => {
    return () => {
      try { popupWindowRef.current?.close(); } catch (_) {}
      popupWindowRef.current = null;
    };
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (clusterGroupRef.current) {
        clusterGroupRef.current.clearLayers();
        clusterGroupRef.current = null;
      }
      markersRef.current = {};
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Renderowanie słupka wirtualnego otworu (SVG)
  const renderVirtualBorehole = () => {
    if (!virtualBorehole) return null;

    const { elevation, gruntBottom, zwietrzelinaBottom, skalaEnd, waterDepth } = virtualBorehole;
    const totalDepth = skalaEnd;
    const svgHeight = 300;
    const svgWidth = 120;
    const barWidth = 50;
    const barX = 10;
    const labelX = barX + barWidth + 5;
    const scale = totalDepth > 0 ? (svgHeight - 40) / totalDepth : 1;

    const zones = [
      { name: 'Grunt', from: 0, to: gruntBottom, color: ZONE_COLORS.grunt },
      { name: 'Zwietrzlina', from: gruntBottom, to: zwietrzelinaBottom, color: ZONE_COLORS.zwietrzelina },
      { name: 'Skała', from: zwietrzelinaBottom, to: skalaEnd, color: ZONE_COLORS.skala },
    ].filter(z => z.to > z.from);

    return (
      <svg width={svgWidth + 80} height={svgHeight + 20} style={{ display: 'block', margin: '0 auto' }}>
        {/* Tytuł */}
        <text x={(svgWidth + 80) / 2} y={14} textAnchor="middle" fontSize={11} fontWeight="bold" fill="#333">
          Wirtualny otwór
        </text>

        {/* Rzędna terenu */}
        <text x={barX} y={30} fontSize={9} fill="#666">
          {elevation.toFixed(1)} m n.p.m.
        </text>

        {/* Strefy */}
        {zones.map((zone, i) => {
          const y = 35 + zone.from * scale;
          const h = (zone.to - zone.from) * scale;
          return (
            <g key={i}>
              <rect
                x={barX}
                y={y}
                width={barWidth}
                height={h}
                fill={zone.color}
                stroke="#333"
                strokeWidth={1}
              />
              {/* Nazwa strefy */}
              {h > 15 && (
                <text
                  x={barX + barWidth / 2}
                  y={y + h / 2 + 4}
                  textAnchor="middle"
                  fontSize={9}
                  fill="white"
                  fontWeight="bold"
                >
                  {zone.name}
                </text>
              )}
              {/* Głębokość po prawej */}
              <text x={labelX} y={y + h} fontSize={9} fill="#333" dominantBaseline="middle">
                {zone.to.toFixed(1)} m
              </text>
              {/* Linia graniczna */}
              <line
                x1={barX}
                y1={y + h}
                x2={labelX - 2}
                y2={y + h}
                stroke="#999"
                strokeWidth={0.5}
                strokeDasharray="2,2"
              />
            </g>
          );
        })}

        {/* Linia górna */}
        <line x1={barX} y1={35} x2={barX + barWidth} y2={35} stroke="#333" strokeWidth={1.5} />

        {/* Zwierciadło wody — niebieska linia + trójkąt z lewej */}
        {waterDepth !== null && waterDepth >= 0 && waterDepth <= skalaEnd && (() => {
          const wy = 35 + waterDepth * scale;
          return (
            <g>
              <line
                x1={barX - 6}
                y1={wy}
                x2={barX + barWidth + 6}
                y2={wy}
                stroke={ZONE_COLORS.woda}
                strokeWidth={2}
              />
              <polygon
                points={`${barX - 9},${wy - 4} ${barX - 3},${wy - 4} ${barX - 6},${wy + 1}`}
                fill={ZONE_COLORS.woda}
              />
              <text
                x={labelX}
                y={wy}
                fontSize={9}
                fill={ZONE_COLORS.woda}
                fontWeight="bold"
                dominantBaseline="middle"
              >
                💧 {waterDepth.toFixed(1)} m
              </text>
            </g>
          );
        })()}
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Nagłówek */}
      <div className="app-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo_giode.png" alt="G-ODE" className="h-6" />
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                Mapa interpolacji geologicznej
              </h1>
              <p className="text-xs text-gray-400">Generator przekrojów geologicznych</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/browse')}
              className="btn-brand-outline flex items-center gap-1"
            >
              <ArrowLeft size={16} />
              Wyszukiwarka
            </button>
            <span className="text-sm text-gray-500">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="btn-brand-outline flex items-center gap-1"
            >
              <LogOut size={16} />
              Wyloguj
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Instrukcja + przycisk */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                <strong>1.</strong> Wybierz projekt i zaznacz min. 3 otwory &nbsp;→&nbsp;
                <strong>2.</strong> Kliknij "Wczytaj dane" &nbsp;→&nbsp;
                <strong>3.</strong> Kliknij w mapę aby wygenerować wirtualny otwór
              </p>
              {selectedCount > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  Zaznaczono: {orderedSelection.map(s => boreholeNameMap[s.id]).join(', ')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedCount > 0 && (
                <button
                  onClick={clearSelection}
                  className="px-3 py-1.5 rounded text-xs bg-gray-100 hover:bg-gray-200 text-gray-600"
                >
                  Wyczyść
                </button>
              )}
              <button
                onClick={handleLoadData}
                disabled={loadingData || selectedCount < 3}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  selectedCount >= 3
                    ? 'bg-brand-700 text-white hover:bg-brand-800'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {loadingData ? 'Ładowanie...' : boreholeData ? 'Odśwież dane' : 'Wczytaj dane'}
              </button>
            </div>
          </div>
          {dataError && (
            <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">{dataError}</div>
          )}
        </div>

        {/* Główny layout: mapa + panel boczny */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Lewa kolumna: projekty + otwory */}
          <div className="space-y-4">
            {/* Projekty */}
            <div className="bg-white rounded-lg shadow-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Projekty</h3>
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Filtruj projekty..."
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              {loadingProjects && <div className="text-gray-500 text-sm py-4 text-center">Ładowanie...</div>}
              {projectsError && <div className="text-red-500 text-sm py-2">{projectsError}</div>}
              {!loadingProjects && projectsLoaded && (
                <div className="space-y-1 max-h-[300px] overflow-y-auto">
                  {projects.length === 0 ? (
                    <div className="text-gray-400 text-sm py-4 text-center">Brak projektów.</div>
                  ) : projects.map(project => (
                    <button
                      key={project.id}
                      onClick={() => selectProject(project)}
                      className={`w-full text-left px-3 py-2 text-sm rounded transition ${
                        selectedProject?.id === project.id
                          ? 'bg-brand-50 text-brand-800 font-semibold'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      {project.title}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Otwory */}
            <div className="bg-white rounded-lg shadow-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Otwory
                {selectedProject && <span className="font-normal text-gray-400 ml-2">— {selectedProject.title}</span>}
              </h3>
              {!selectedProject ? (
                <div className="text-gray-400 text-sm py-4 text-center">Wybierz projekt.</div>
              ) : loadingBoreholes ? (
                <div className="text-gray-400 text-sm py-4 text-center">Ładowanie...</div>
              ) : boreholesError ? (
                <div className="text-red-400 text-sm py-2">{boreholesError}</div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    {boreholes.length > 5 && (
                      <input
                        type="text"
                        placeholder="Filtruj otwory..."
                        value={boreholeFilter}
                        onChange={(e) => setBoreholeFilter(e.target.value)}
                        className="flex-1 border rounded px-2 py-2 text-sm"
                      />
                    )}
                    <button
                      onClick={selectedCount === boreholes.length ? clearSelection : selectAllBoreholes}
                      className="px-3 py-2 rounded text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 whitespace-nowrap"
                    >
                      {selectedCount === boreholes.length ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                    </button>
                  </div>
                  {Object.keys(boreholeZoneInfo).length > 0 && (
                    <div className="flex gap-1 mb-2">
                      {[
                        { key: 'hasWater', label: '💧', color: ZONE_COLORS.woda, title: 'Woda' },
                        { key: 'hasGrunt', label: 'G', color: ZONE_COLORS.grunt, title: 'Grunt' },
                        { key: 'hasZwietrzelina', label: 'Z', color: ZONE_COLORS.zwietrzelina, title: 'Zwietrzlina' },
                        { key: 'hasSkala', label: 'S', color: ZONE_COLORS.skala, title: 'Skała' },
                        { key: 'allThree', label: 'GZS', color: '#333', title: 'Wszystkie 3 strefy' },
                      ].map(f => {
                        const matchingIds = boreholes.filter(b => boreholeZoneInfo[b.id]?.[f.key]).map(b => b.id);
                        return (
                          <button
                            key={f.key}
                            onClick={() => { clearSelection(); setTimeout(() => addBoreholesByIds(matchingIds), 0); }}
                            className="px-1.5 py-0.5 rounded text-xs border hover:opacity-80"
                            style={{ borderColor: f.color, color: f.color }}
                            title={`Zaznacz: ${f.title} (${matchingIds.length})`}
                          >
                            {f.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mb-2" style={{ lineHeight: '1.4' }}>
                    Shift + przeciągnij na mapie = zaznacz grupę otworów
                  </div>
                  <div className="space-y-1 max-h-[300px] overflow-y-auto">
                    {boreholes.map(borehole => (
                      <label
                        key={borehole.id}
                        className={`flex items-center px-3 py-2 text-sm rounded cursor-pointer transition ${
                          selectedBoreholeIds.has(borehole.id)
                            ? 'bg-green-50 border border-green-300'
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedBoreholeIds.has(borehole.id)}
                          onChange={() => toggleBoreholeSelection(borehole.id)}
                          className="mr-3 w-4 h-4 accent-green-600"
                        />
                        <span className="flex-1">{borehole.nameBorehole || '(brak nazwy)'}</span>
                        {/* Info o strefach — po wczytaniu danych */}
                        {boreholeZoneInfo[borehole.id] && (
                          boreholeZoneInfo[borehole.id].error ? (
                            <span className="text-xs text-gray-400 ml-2" title="Brak danych warstw w bazie">brak danych</span>
                          ) : (
                            <span className="flex items-center gap-1 ml-2">
                              <span
                                className="inline-block w-3 h-3 rounded-sm border"
                                style={{
                                  background: boreholeZoneInfo[borehole.id].hasGrunt ? ZONE_COLORS.grunt : 'transparent',
                                  borderColor: boreholeZoneInfo[borehole.id].hasGrunt ? ZONE_COLORS.grunt : '#ccc',
                                }}
                                title="Grunt"
                              />
                              <span
                                className="inline-block w-3 h-3 rounded-sm border"
                                style={{
                                  background: boreholeZoneInfo[borehole.id].hasZwietrzelina ? ZONE_COLORS.zwietrzelina : 'transparent',
                                  borderColor: boreholeZoneInfo[borehole.id].hasZwietrzelina ? ZONE_COLORS.zwietrzelina : '#ccc',
                                }}
                                title="Zwietrzlina"
                              />
                              <span
                                className="inline-block w-3 h-3 rounded-sm border"
                                style={{
                                  background: boreholeZoneInfo[borehole.id].hasSkala ? ZONE_COLORS.skala : 'transparent',
                                  borderColor: boreholeZoneInfo[borehole.id].hasSkala ? ZONE_COLORS.skala : '#ccc',
                                }}
                                title="Skała"
                              />
                              <span
                                className="inline-block w-3 h-3 rounded-sm border"
                                style={{
                                  background: boreholeZoneInfo[borehole.id].hasWater ? ZONE_COLORS.woda : 'transparent',
                                  borderColor: boreholeZoneInfo[borehole.id].hasWater ? ZONE_COLORS.woda : '#ccc',
                                }}
                                title={boreholeZoneInfo[borehole.id].hasWater ? `Woda (zust: ${boreholeZoneInfo[borehole.id].waterZust} m)` : 'Brak wody'}
                              />
                              {!boreholeZoneInfo[borehole.id].allThree && (
                                <span className="text-xs text-red-500 ml-1" title="Brak wszystkich 3 stref">!</span>
                              )}
                            </span>
                          )
                        )}
                        {borehole.coordZ != null && (
                          <span className="text-xs text-gray-400 ml-2">{borehole.coordZ} m</span>
                        )}
                      </label>
                    ))}
                    <div className="text-gray-400 text-xs pt-2">
                      Znaleziono: {boreholes.length} otwór(ów)
                      {zoneInfoLoading && <span className="ml-2 text-purple-500">— sprawdzanie stref...</span>}
                    </div>
                    {Object.keys(boreholeZoneInfo).length > 0 && (
                      <div className="mt-2 pt-2 border-t text-xs space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: ZONE_COLORS.grunt }} />
                          <span>Grunt</span>
                          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: ZONE_COLORS.zwietrzelina }} />
                          <span>Zwietrzlina</span>
                          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: ZONE_COLORS.skala }} />
                          <span>Skała</span>
                          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: ZONE_COLORS.woda }} />
                          <span>Woda</span>
                        </div>
                        {(() => {
                          const total = Object.keys(boreholeZoneInfo).length;
                          const ok = Object.values(boreholeZoneInfo).filter(z => z.allThree).length;
                          const bad = total - ok;
                          return (
                            <div className={bad > 0 ? 'text-orange-600' : 'text-green-600'}>
                              {ok}/{total} otworów ma wszystkie 3 strefy
                              {bad > 0 && ` (${bad} niekompletnych — oznaczone !)`}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Środkowa + prawa kolumna: mapa + wynik */}
          <div className="lg:col-span-2 space-y-4">
            {/* Mapa */}
            <div className="bg-white rounded-lg shadow-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <MapPin size={14} className="text-brand-700" />
                Mapa otworów
                {boreholeData && (
                  <span className="text-xs font-normal text-green-600 ml-2">
                    — kliknij w mapę aby wygenerować wirtualny otwór
                  </span>
                )}
              </h3>
              {(() => {
                const mapVis = selectedProject && !loadingBoreholes && mapPoints && mapPoints.length > 0;
                return (
                  <>
                    <div
                      ref={mapContainerRef}
                      style={mapVis
                        ? { height: 500, width: '100%', borderRadius: 8, cursor: boreholeData ? 'crosshair' : 'default' }
                        : { height: 0, width: '100%', overflow: 'hidden' }}
                    />
                    {!mapVis && (
                      <div className="flex items-center justify-center bg-gray-50 rounded-lg" style={{ height: 500 }}>
                        <p className="text-gray-400 text-sm">
                          {!selectedProject
                            ? 'Wybierz projekt aby zobaczyć mapę otworów.'
                            : loadingBoreholes
                              ? 'Ładowanie otworów...'
                              : 'Brak otworów ze współrzędnymi w tym projekcie.'}
                        </p>
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="text-xs text-gray-400 mt-1">
                Kliknij otwór (punkt) aby zaznaczyć/odznaczyć. {boreholeData ? 'Kliknij puste miejsce aby interpolować.' : ''}
                {tileDrawMode && <span className="ml-2 text-pink-700 font-medium">📐 Tryb obszaru NMT — przeciągnij prostokąt na mapie</span>}
              </div>
            </div>

            {/* Panel Tile NMT — zarządzanie pobranymi obszarami NMT z GUGiK.
                Dostępny od razu po wyborze projektu (nie wymaga wczytania danych otworów) */}
            {selectedProject && allBoreholes && allBoreholes.length > 0 && (
              <div className="bg-white rounded-lg shadow-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    <span className="text-pink-600">📐</span>
                    Tile NMT (GUGiK)
                    <span className="text-xs font-normal text-gray-400 ml-2">
                      — pobierz raz obszar, używaj do generowania gridu
                    </span>
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setTileDrawMode(m => !m);
                        if (tileDrawMode) setTileDrawBbox(null);
                      }}
                      disabled={tileFetching}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition ${
                        tileDrawMode
                          ? 'bg-pink-600 text-white hover:bg-pink-700'
                          : 'bg-pink-100 text-pink-700 hover:bg-pink-200'
                      } disabled:opacity-50`}
                    >
                      {tileDrawMode ? '✕ Anuluj rysowanie' : '📐 Zaznacz obszar NMT'}
                    </button>
                  </div>
                </div>

                {/* Zatwierdzony bbox + pobranie */}
                {tileDrawBbox && (
                  <div className="bg-pink-50 border border-pink-200 rounded p-3 mb-3">
                    <div className="text-xs text-gray-700 mb-2">
                      <b>Zaznaczony obszar:</b> {(tileDrawBbox.maxX - tileDrawBbox.minX).toFixed(0)} × {(tileDrawBbox.maxY - tileDrawBbox.minY).toFixed(0)} m
                      <span className="text-gray-500 ml-2">
                        ({tileDrawBbox.minX.toFixed(0)}–{tileDrawBbox.maxX.toFixed(0)} × {tileDrawBbox.minY.toFixed(0)}–{tileDrawBbox.maxY.toFixed(0)})
                      </span>
                    </div>
                    {(() => {
                      const w = tileDrawBbox.maxX - tileDrawBbox.minX;
                      const h = tileDrawBbox.maxY - tileDrawBbox.minY;
                      const resX = Math.round(w / tileDrawStep) + 1;
                      const resY = Math.round(h / tileDrawStep) + 1;
                      const total = resX * resY;
                      const sec = Math.ceil((total / 150) * 0.5);
                      return (
                        <div className="flex items-center gap-3 flex-wrap text-xs">
                          <div className="flex items-center gap-1">
                            <label className="text-gray-500">Krok:</label>
                            <select
                              value={tileDrawStep}
                              onChange={(e) => setTileDrawStep(Number(e.target.value))}
                              disabled={tileFetching}
                              className="border rounded px-2 py-1 disabled:opacity-50"
                            >
                              <option value={1}>1 m (NMT raster)</option>
                              <option value={2}>2 m</option>
                              <option value={5}>5 m</option>
                              <option value={10}>10 m</option>
                              <option value={25}>25 m</option>
                            </select>
                          </div>
                          <span className="text-gray-500">
                            {resX}×{resY} = {total.toLocaleString()} pkt
                          </span>
                          <span className="text-amber-700">⏱️ ~{sec >= 60 ? Math.ceil(sec / 60) + ' min' : sec + ' s'}</span>
                          <button
                            onClick={handleFetchTile}
                            disabled={tileFetching || total > 500000}
                            className="px-3 py-1.5 rounded font-medium bg-brand-700 text-white hover:bg-brand-800 disabled:opacity-50"
                          >
                            {tileFetching
                              ? `Pobieranie... (postęp w pasku u góry)`
                              : 'Pobierz NMT z GUGiK'}
                          </button>
                          <button
                            onClick={() => setTileDrawBbox(null)}
                            disabled={tileFetching}
                            className="px-2 py-1.5 rounded text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                          >
                            Wyczyść
                          </button>
                          {total > 500000 && (
                            <span className="text-red-600 text-xs">⚠️ Za duży obszar (max 500k pkt) — zmniejsz lub zwiększ krok</span>
                          )}
                        </div>
                      );
                    })()}
                    {tileFetchError && (
                      <div className="mt-2 text-xs text-red-700">⚠️ {tileFetchError}</div>
                    )}
                  </div>
                )}

                {/* Lista istniejących tile */}
                {tilesLoading ? (
                  <div className="text-xs text-gray-400">Ładowanie listy tile...</div>
                ) : tiles.length === 0 ? (
                  <div className="text-xs text-gray-400">Brak pobranych tile NMT dla tego projektu. Zaznacz obszar i pobierz.</div>
                ) : (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{tiles.length} tile w cache (kolor obok = obwódka na mapie):</div>
                    <div className="space-y-1">
                      {tiles.map((t, idx) => {
                        const w = t.bbox.maxX - t.bbox.minX;
                        const h = t.bbox.maxY - t.bbox.minY;
                        const resX = Math.round(w / t.step) + 1;
                        const resY = Math.round(h / t.step) + 1;
                        const color = tileColorAt(idx);
                        return (
                          <div key={t.fileName} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                            <span className="text-gray-700 flex items-center gap-2">
                              <span
                                className="inline-block w-3 h-3 rounded-sm border"
                                style={{ backgroundColor: color, borderColor: color }}
                                title="Kolor obwódki tego tile na mapie"
                              />
                              <b>{w.toFixed(0)}×{h.toFixed(0)} m</b>
                              <span className="text-gray-500 ml-1">krok {t.step} m, ~{(resX * resY).toLocaleString()} pkt</span>
                            </span>
                            <button
                              onClick={() => handleDeleteTile(t.fileName)}
                              className="px-2 py-0.5 text-xs text-red-600 hover:bg-red-50 rounded"
                            >
                              🗑️ usuń
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Panel wirtualnego otworu — renderowany w popupie (nowe okno) lub inline jako fallback */}
            {virtualBorehole && clickCoords && !popupOpen && !popupBlocked && (
              <div className="bg-white rounded-lg shadow-lg p-3 text-sm text-gray-600 flex items-center gap-3">
                <MousePointer size={14} className="text-red-500" />
                <span>Wirtualny otwór otwarty w osobnym oknie.</span>
                <button
                  type="button"
                  onClick={() => {
                    // Synchroniczne window.open w odpowiedzi na kliknięcie przycisku (user gesture).
                    const w = popupWindowRef.current;
                    if (!w || w.closed) {
                      const newW = window.open('', '_blank', 'width=720,height=640,menubar=no,toolbar=no,location=no,status=no');
                      if (newW) {
                        popupWindowRef.current = newW;
                        setPopupOpen(true);
                      } else {
                        popupBlockedRef.current = true;
                        setPopupBlocked(true);
                      }
                    } else {
                      try { w.focus(); } catch (_) {}
                      setPopupOpen(true);
                    }
                  }}
                  className="ml-auto px-3 py-1 rounded text-xs bg-brand-700 text-white hover:bg-brand-800"
                >
                  Otwórz ponownie
                </button>
              </div>
            )}
            {virtualBorehole && clickCoords && (popupOpen || popupBlocked) && (() => {
              const panel = (
              <div className="bg-white rounded-lg shadow-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1">
                  <MousePointer size={14} className="text-red-500" />
                  Wirtualny otwór (interpolacja)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* SVG słupek */}
                  <div>
                    {renderVirtualBorehole()}
                  </div>

                  {/* Dane liczbowe */}
                  <div className="text-sm space-y-2">
                    <div className="text-xs text-gray-400 mb-3">
                      Współrzędne PL-2000: X={clickCoords.coordX}, Y={clickCoords.coordY}
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ background: ZONE_COLORS.grunt }} />
                      <span className="font-medium">Grunt:</span>
                      {virtualBorehole.gruntPresence > 0.01 ? (
                        <>
                          <span>0 – {virtualBorehole.gruntBottom.toFixed(1)} m</span>
                          <span className="text-gray-400 text-xs">({virtualBorehole.gruntBottom.toFixed(1)} m)</span>
                          {virtualBorehole.gruntPresence < 0.95 && (
                            <span className="text-orange-500 text-xs">(wyklin {Math.round(virtualBorehole.gruntPresence * 100)}%)</span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 italic">brak w tym rejonie</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ background: ZONE_COLORS.zwietrzelina }} />
                      <span className="font-medium">Zwietrzlina:</span>
                      {virtualBorehole.zwietPresence > 0.01 ? (
                        <>
                          <span>{virtualBorehole.gruntBottom.toFixed(1)} – {virtualBorehole.zwietrzelinaBottom.toFixed(1)} m</span>
                          <span className="text-gray-400 text-xs">({(virtualBorehole.zwietrzelinaBottom - virtualBorehole.gruntBottom).toFixed(1)} m)</span>
                          {virtualBorehole.zwietPresence < 0.95 && (
                            <span className="text-orange-500 text-xs">(wyklin {Math.round(virtualBorehole.zwietPresence * 100)}%)</span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 italic">brak w tym rejonie</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ background: ZONE_COLORS.skala }} />
                      <span className="font-medium">Skała:</span>
                      <span>od {virtualBorehole.zwietrzelinaBottom.toFixed(1)} m</span>
                      <span className="text-gray-400 text-xs">(wyświetlono 10 m)</span>
                    </div>

                    {virtualBorehole.waterDepth !== null && (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ background: ZONE_COLORS.woda }} />
                        <span className="font-medium">Zwierciadło wody:</span>
                        <span>{virtualBorehole.waterDepth.toFixed(1)} m p.p.t.</span>
                        <span className="text-gray-400 text-xs">
                          ({virtualBorehole.waterElevation.toFixed(1)} m n.p.m.)
                        </span>
                      </div>
                    )}

                    <div className="border-t pt-2 mt-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Rzędna terenu:</span>
                        <span>{virtualBorehole.elevation.toFixed(1)} m n.p.m.</span>
                      </div>
                    </div>

                    {/* Diagnostyka — dane źródłowe (domyślnie zwinięta) */}
                    {virtualBorehole.diagnostics && (
                      <details className="border-t pt-2 mt-3">
                        <summary className="text-xs font-semibold text-gray-400 cursor-pointer hover:text-gray-600 select-none">
                          Dane źródłowe (sortowane wg odległości)
                        </summary>
                        <table className="w-full text-xs mt-1">
                          <thead>
                            <tr className="text-gray-400 border-b">
                              <th className="text-left py-1">Otwór</th>
                              <th className="text-right py-1">Odl. (m)</th>
                              <th className="text-right py-1">Rzędna</th>
                              <th className="text-right py-1">Grunt</th>
                              <th className="text-right py-1">Zwietrz.</th>
                              <th className="text-center py-1">Strefy</th>
                            </tr>
                          </thead>
                          <tbody>
                            {virtualBorehole.diagnostics.map((d, i) => (
                              <tr key={i} className="border-b border-gray-50">
                                <td className="py-1 font-medium">{d.name}</td>
                                <td className="py-1 text-right">{d.dist}</td>
                                <td className="py-1 text-right">{d.elevation}</td>
                                <td className="py-1 text-right">{d.gruntBottom}</td>
                                <td className="py-1 text-right">{d.zwietrzelinaBottom}</td>
                                <td className="py-1 text-center">
                                  <span title="Grunt" style={{ color: d.hasGrunt ? ZONE_COLORS.grunt : '#ccc' }}>G</span>
                                  <span title="Zwietrzlina" style={{ color: d.hasZwietrzelina ? ZONE_COLORS.zwietrzelina : '#ccc' }}>Z</span>
                                  <span title="Skała" style={{ color: d.hasSkala ? ZONE_COLORS.skala : '#ccc' }}>S</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="text-xs text-gray-400 mt-2">
                          Interpolacja (power=2) z wyklinem stref, na podstawie {boreholeData?.length} otworów.
                          Głębokości podane od powierzchni terenu. Kolumna "Strefy": kolorowe = otwór ma strefę, szare = brak.
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
              );
              if (popupBlocked) return panel;
              return (
                <PopupWindow
                  windowInstance={popupWindowRef.current}
                  title={`Wirtualny otwór – X=${clickCoords.coordX}, Y=${clickCoords.coordY}`}
                  onClose={() => {
                    popupWindowRef.current = null;
                    setPopupOpen(false);
                  }}
                >
                  {panel}
                </PopupWindow>
              );
            })()}

            {/* Info o załadowanych danych */}
            {boreholeData && !virtualBorehole && (
              <div className="bg-purple-50 rounded-lg p-4 text-sm text-purple-800">
                Dane załadowane dla {boreholeData.length} otworów. Kliknij w dowolne miejsce na mapie aby wygenerować wirtualny otwór.
              </div>
            )}
          </div>
        </div>

        {/* ===== SEKCJA GRID 3D ===== */}
        {boreholeData && boreholeData.length >= 3 && (
          <div
            className="bg-white rounded-lg shadow-lg p-4 mt-4"
            style={gridIsFullscreen ? {
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 9999,
              borderRadius: 0,
              overflow: 'auto',
              margin: 0,
            } : undefined}
          >
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Layers size={14} className="text-brand-700" />
              Grid 3D — powierzchnie stropów warstw
              {gridIsFullscreen && (
                <button
                  onClick={toggleGridFullscreen}
                  className="ml-auto px-3 py-1 rounded text-xs bg-slate-700 text-white hover:bg-slate-800"
                  title="Wyjdź z pełnego ekranu (ESC)"
                >
                  ⤓ Wyjdź (ESC)
                </button>
              )}
            </h3>

            {/* Kontrolki */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Kształt:</label>
                <div className="flex border rounded overflow-hidden text-xs">
                  <button
                    type="button"
                    onClick={() => setGridShapeMode('square')}
                    title="Grid jako kwadrat wymuszony z dłuższego boku otworów"
                    className={`px-2 py-1 transition ${gridShapeMode === 'square' ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                  >
                    Kwadrat
                  </button>
                  <button
                    type="button"
                    onClick={() => setGridShapeMode('rect')}
                    title="Grid = bbox otworów (mniejszy obszar — łatwiej pokryć tile NMT)"
                    className={`px-2 py-1 transition ${gridShapeMode === 'rect' ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                  >
                    Prostokąt
                  </button>
                </div>
              </div>
              {/* Bufor — wyłączony na razie. Maska danych (convex hull + dist) zastępuje tę funkcjonalność.
              <div className="flex items-center gap-2">
                <label className={`text-xs ${gridShapeMode === 'square' ? 'text-gray-300' : 'text-gray-500'}`}>Bufor:</label>
                <select
                  value={gridBuffer}
                  onChange={(e) => setGridBuffer(Number(e.target.value))}
                  disabled={gridShapeMode === 'square'}
                  title={gridShapeMode === 'square' ? 'W trybie Kwadrat bufor = 0 (kwadrat już rozszerza krótszy bok)' : 'Bufor w metrach dodawany z każdej strony prostokąta'}
                  className="border rounded px-2 py-1 text-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  <option value={5}>5 m</option>
                  <option value={10}>10 m</option>
                  <option value={20}>20 m</option>
                  <option value={50}>50 m</option>
                  <option value={100}>100 m</option>
                  <option value={200}>200 m</option>
                </select>
              </div>
              */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Tryb:</label>
                <div className="flex border rounded overflow-hidden text-xs">
                  <button
                    type="button"
                    onClick={() => setGridSizeMode('points')}
                    className={`px-2 py-1 transition ${gridSizeMode === 'points' ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                  >
                    Liczba punktów
                  </button>
                  <button
                    type="button"
                    onClick={() => setGridSizeMode('step')}
                    className={`px-2 py-1 transition ${gridSizeMode === 'step' ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                  >
                    Krok [m]
                  </button>
                </div>
              </div>
              {gridSizeMode === 'points' ? (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Rozdzielczość grida:</label>
                  <select
                    value={gridResolution}
                    onChange={(e) => setGridResolution(Number(e.target.value))}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    <option value={20}>20 x 20</option>
                    <option value={30}>30 x 30</option>
                    <option value={40}>40 x 40</option>
                    <option value={50}>50 x 50</option>
                    <option value={80}>80 x 80</option>
                  </select>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Krok siatki:</label>
                  <select
                    value={gridStep}
                    onChange={(e) => setGridStep(Number(e.target.value))}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    <option value={1}>1 m (NMT raster)</option>
                    <option value={2}>2 m</option>
                    <option value={5}>5 m</option>
                    <option value={10}>10 m</option>
                    <option value={25}>25 m</option>
                    <option value={50}>50 m</option>
                    <option value={100}>100 m</option>
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500" title="Tnij komórki gridu dalej niż X m od najbliższego otworu (usuwa puste obszary IDW)">Maska danych:</label>
                <select
                  value={gridMaskDistance === null ? 'off' : String(gridMaskDistance)}
                  onChange={(e) => setGridMaskDistance(e.target.value === 'off' ? null : Number(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="off">Wył. (cały bbox)</option>
                  <option value="10">10 m od otworu</option>
                  <option value="30">30 m od otworu</option>
                  <option value="50">50 m od otworu</option>
                  <option value="75">75 m od otworu</option>
                  <option value="100">100 m od otworu</option>
                  <option value="200">200 m od otworu</option>
                  <option value="500">500 m od otworu</option>
                </select>
              </div>
              {/* Top-K — przyspieszenie IDW. Default = Wszystkie (zero zmiany w wynikach). */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500" title="IDW liczone tylko z K najbliższych otworów. Zmniejsza dokładność o ułamek metra, ale przyspiesza ~10×. 'Wszystkie' = identyczne wyniki jak dotychczas.">Sąsiedzi IDW:</label>
                <select
                  value={gridTopK === null ? 'all' : String(gridTopK)}
                  onChange={(e) => setGridTopK(e.target.value === 'all' ? null : Number(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="all">Wszystkie (dokładnie)</option>
                  <option value="20">20 najbliższych</option>
                  <option value="12">12 najbliższych</option>
                  <option value="8">8 najbliższych (szybko)</option>
                  <option value="4">4 najbliższe (b. szybko)</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Wyklin:</label>
                <select
                  value={wedgeDisplayMode}
                  onChange={(e) => setWedgeDisplayMode(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="merge">Zlanie (ciągła pow.)</option>
                  <option value="hole">Dziura (brak strefy)</option>
                </select>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none" title="Pierwsza powierzchnia (teren) liczona z prawdziwego NMT z GUGiK na całej siatce, zamiast IDW z otworów. Strefy poniżej dalej IDW. Wymaga internetu — pobiera kilkanaście sek.–min.">
                <input
                  type="checkbox"
                  checked={useNMTTerrain}
                  onChange={(e) => setUseNMTTerrain(e.target.checked)}
                  className="cursor-pointer"
                />
                ⛰️ Teren z NMT (GUGiK)
              </label>
              <button
                onClick={handleGenerateGrid}
                disabled={generatingGrid}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-700 text-white hover:bg-brand-800 transition disabled:opacity-50"
              >
                {generatingGrid ? 'Generowanie...' : gridData ? 'Przelicz grid' : 'Generuj grid 3D'}
              </button>
              {nmtTerrainError && (
                <pre className="text-xs text-red-700 ml-2 whitespace-pre-wrap font-mono bg-red-50 border border-red-200 rounded px-2 py-1 max-w-2xl">⚠️ {nmtTerrainError}</pre>
              )}

              {gridData && (
                <div className="flex items-center gap-4 ml-2">
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-gray-500 whitespace-nowrap">XY:</label>
                    <input
                      type="range" min="0.5" max="3" step="0.1" value={aspectXY}
                      onChange={(e) => setAspectXY(Number(e.target.value))}
                      className="w-20 h-4"
                    />
                    <span className="text-xs text-gray-400 w-8">{aspectXY.toFixed(1)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-gray-500 whitespace-nowrap">Z:</label>
                    <input
                      type="range" min="0.1" max="2" step="0.1" value={aspectZ}
                      onChange={(e) => setAspectZ(Number(e.target.value))}
                      className="w-20 h-4"
                    />
                    <span className="text-xs text-gray-400 w-8">{aspectZ.toFixed(1)}</span>
                  </div>
                  <label className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer select-none ml-2" title="Pokaż/ukryj nazwy otworów na wykresie 3D">
                    <input
                      type="checkbox"
                      checked={showBoreholeNames}
                      onChange={(e) => setShowBoreholeNames(e.target.checked)}
                      className="cursor-pointer"
                    />
                    🏷️ Nazwy otworów
                  </label>
                </div>
              )}

              {gridData && (
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-xs text-gray-400">Pobierz CSV:</span>
                  <button
                    onClick={() => downloadGridCSV('grunt')}
                    className="px-2 py-1 rounded text-xs border hover:bg-gray-50"
                    style={{ borderColor: ZONE_COLORS.grunt, color: ZONE_COLORS.grunt }}
                  >
                    Grunt
                  </button>
                  <button
                    onClick={() => downloadGridCSV('zwietrzelina')}
                    className="px-2 py-1 rounded text-xs border hover:bg-gray-50"
                    style={{ borderColor: ZONE_COLORS.zwietrzelina, color: ZONE_COLORS.zwietrzelina }}
                  >
                    Zwietrzlina
                  </button>
                  <button
                    onClick={() => downloadGridCSV('skala')}
                    className="px-2 py-1 rounded text-xs border hover:bg-gray-50"
                    style={{ borderColor: ZONE_COLORS.skala, color: ZONE_COLORS.skala }}
                  >
                    Skala
                  </button>
                  {gridData.waterSurface && (
                    <button
                      onClick={() => downloadGridCSV('woda')}
                      className="px-2 py-1 rounded text-xs border hover:bg-gray-50"
                      style={{ borderColor: ZONE_COLORS.woda, color: ZONE_COLORS.woda }}
                      title="Eksport zwierciadła wody (rzędne m n.p.m.)"
                    >
                      Woda
                    </button>
                  )}
                  <button
                    onClick={downloadAllGridCSV}
                    className="px-3 py-1 rounded text-xs bg-gray-700 text-white hover:bg-gray-800"
                  >
                    Wszystkie (1 plik)
                  </button>
                  <span className="text-xs text-gray-300 mx-1">|</span>
                  <span className="text-xs text-gray-400">STL:</span>
                  <button
                    onClick={() => downloadLayerSTL('grunt')}
                    className="px-2 py-1 rounded text-xs border hover:bg-gray-50"
                    style={{ borderColor: ZONE_COLORS.grunt, color: ZONE_COLORS.grunt }}
                  >
                    Grunt
                  </button>
                  <button
                    onClick={() => downloadLayerSTL('zwietrzelina')}
                    className="px-2 py-1 rounded text-xs border hover:bg-gray-50"
                    style={{ borderColor: ZONE_COLORS.zwietrzelina, color: ZONE_COLORS.zwietrzelina }}
                  >
                    Zwietrzlina
                  </button>
                  <button
                    onClick={() => downloadLayerSTL('skala')}
                    className="px-2 py-1 rounded text-xs border hover:bg-gray-50"
                    style={{ borderColor: ZONE_COLORS.skala, color: ZONE_COLORS.skala }}
                  >
                    Skala
                  </button>
                  <button
                    onClick={downloadSTL}
                    className="px-3 py-1 rounded text-xs bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    Wszystko
                  </button>
                  <span className="text-xs text-gray-300 mx-1">|</span>
                  <button
                    onClick={downloadHTML3D}
                    className="px-3 py-1 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    Pobierz HTML 3D
                  </button>
                  <button
                    onClick={toggleGridFullscreen}
                    className="px-3 py-1 rounded text-xs bg-slate-700 text-white hover:bg-slate-800"
                    title="Powiększ wykres na cały ekran (ESC żeby wyjść)"
                  >
                    {gridIsFullscreen ? '⤓ Wyjdź z pełnego ekranu' : '⛶ Pełny ekran'}
                  </button>
                </div>
              )}
            </div>

            {/* Panel danych geofizycznych — chmury punktów X Y Z value, wyświetlane razem z modelem 3D */}
            <div className="text-xs mb-2 p-2 bg-purple-50 border border-purple-200 rounded">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-medium text-purple-700">📊 Dane geofizyczne</span>
                {geoPhysFiles.length > 0 && (
                  <span className="text-gray-500">— {geoPhysFiles.length} plik{geoPhysFiles.length === 1 ? '' : geoPhysFiles.length < 5 ? 'i' : 'ów'}</span>
                )}
                <label className="px-3 py-1 rounded bg-purple-600 text-white hover:bg-purple-700 cursor-pointer ml-auto">
                  + Wczytaj plik(i) .txt
                  <input
                    type="file"
                    accept=".txt,.csv,.xyz,.dat"
                    multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      e.target.value = '';
                      // Sekwencyjnie żeby błędy się nie nadpisywały i kolejność była zachowana
                      for (const f of files) {
                        await handleGeoPhysUpload(f);
                      }
                    }}
                    className="hidden"
                  />
                </label>
                {geoPhysFiles.length === 0 && (
                  <span className="text-gray-500">Format: 4 kolumny X Y Z wartość (separator: spacja/tab)</span>
                )}
              </div>
              {geoPhysError && (
                <div className="text-red-700 mb-1">⚠️ {geoPhysError}</div>
              )}
              {/* Wspólne kontrolki skali kolorów — jeden colorbar dla wszystkich plików */}
              {geoPhysFiles.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap py-1 border-t border-purple-100">
                  <label className="flex items-center gap-1 cursor-pointer select-none" title="Czerwona linia tam gdzie profil geofizyczny ma punkty na rzędnej stropu skały (tolerancja ±2 m)">
                    <input
                      type="checkbox"
                      checked={showProfileIntersections}
                      onChange={(e) => setShowProfileIntersections(e.target.checked)}
                      className="cursor-pointer"
                    />
                    <span className="text-red-600 font-medium">🔴 Część wspólna ze stropem skały</span>
                  </label>
                  <span className="text-gray-300">|</span>
                  <span className="text-purple-700 font-medium">Skala (wspólna):</span>
                  <select
                    value={geoPhysColorscale}
                    onChange={(e) => setGeoPhysColorscale(e.target.value)}
                    className="border rounded px-1 py-0.5"
                  >
                    {COLORSCALE_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <label className="text-gray-500">Min:</label>
                  <input
                    type="number" step="0.01"
                    value={geoPhysFilterMin}
                    onChange={(e) => setGeoPhysFilterMin(Number(e.target.value))}
                    className="border rounded px-1 py-0.5 w-20"
                  />
                  <label className="text-gray-500">Max:</label>
                  <input
                    type="number" step="0.01"
                    value={geoPhysFilterMax}
                    onChange={(e) => setGeoPhysFilterMax(Number(e.target.value))}
                    className="border rounded px-1 py-0.5 w-20"
                  />
                </div>
              )}
              {/* Lista plików — per plik tylko widoczność/krycie */}
              {geoPhysFiles.map(f => (
                <div key={f.id} className="flex items-center gap-2 flex-wrap py-1 border-t border-purple-100">
                  <input
                    type="checkbox"
                    checked={f.visible}
                    onChange={(e) => updateGeoPhysFile(f.id, { visible: e.target.checked })}
                    className="cursor-pointer"
                    title="Pokaż/ukryj na wykresie"
                  />
                  <span className="text-gray-700 font-medium">{f.fileName}</span>
                  <span className="text-gray-500">{f.count.toLocaleString()} pkt, {f.min.toFixed(1)}–{f.max.toFixed(1)}</span>
                  <span className="text-gray-300">|</span>
                  <label className="text-gray-500">Krycie:</label>
                  <input
                    type="range" min="0.1" max="1" step="0.1"
                    value={f.opacity}
                    onChange={(e) => updateGeoPhysFile(f.id, { opacity: Number(e.target.value) })}
                    className="w-14 h-4"
                  />
                  <span className="text-gray-500 w-8">{f.opacity.toFixed(1)}</span>
                  <button
                    onClick={() => removeGeoPhysFile(f.id)}
                    className="px-2 py-0.5 rounded text-red-600 hover:bg-red-50 ml-auto"
                    title="Usuń ten plik"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>

            {/* Podgląd parametrów siatki */}
            {gridBBox && gridBBox.rangeX > 0 && gridBBox.rangeY > 0 && (
              <div className="text-xs text-gray-500 mb-3 flex items-center gap-3 flex-wrap">
                <span>
                  Siatka: <b>{effectiveResX}×{effectiveResY}</b> = {(effectiveResX * effectiveResY).toLocaleString()} punktów
                </span>
                <span className="text-gray-300">|</span>
                <span>
                  Obszar: <b>{gridBBox.rangeX.toFixed(0)} × {gridBBox.rangeY.toFixed(0)} m</b>
                </span>
                <span className="text-gray-300">|</span>
                <span>
                  Krok: ~{(gridBBox.rangeX / (effectiveResX - 1)).toFixed(1)} × {(gridBBox.rangeY / (effectiveResY - 1)).toFixed(1)} m
                </span>
                {gridMaskDistance !== null && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-emerald-700">Maska: tnij dalej niż {gridMaskDistance} m od otworu</span>
                  </>
                )}
                {gridTopK !== null && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-blue-700">IDW: top-{gridTopK} (przybliżenie)</span>
                  </>
                )}
                {(effectiveResX * effectiveResY) > 40000 && (
                  <span className="text-orange-600 font-medium">⚠️ duża siatka — generowanie może być wolne</span>
                )}
                {useNMTTerrain && (() => {
                  if (!gridBBox) return null;
                  const tile = findCoveringTile(tiles, gridBBox);
                  return (
                    <>
                      <span className="text-gray-300">|</span>
                      {tile
                        ? <span className="text-green-700" title={`Tile ${tile.fileName} — bilinearne próbkowanie z kroku ${tile.step} m`}>✓ Tile NMT pokrywa obszar (krok {tile.step} m)</span>
                        : <span className="text-red-600 font-medium">⚠️ Brak tile NMT — zaznacz obszar na mapie i pobierz</span>
                      }
                    </>
                  );
                })()}
              </div>
            )}

            {/* Wykres 3D */}
            {gridData && (() => {
              // Przygotuj powierzchnie z uwzględnieniem trybu wyklinu
              const PRESENCE_THRESHOLD = 0.05;
              const isHoleMode = wedgeDisplayMode === 'hole';

              const displayZwietSurface = isHoleMode && gridData.zwietPresenceSurface
                ? gridData.zwietrzelinaSurface.map((row, j) =>
                    row.map((val, i) => gridData.zwietPresenceSurface[j][i] < PRESENCE_THRESHOLD ? null : val)
                  )
                : gridData.zwietrzelinaSurface;

              // Skała zawsze ciągła — nigdy nie ma dziur
              const displaySkalaSurface = gridData.skalaSurface;

              // ===== Przecięcia profili geofizycznych z warstwami =====
              // Bilinear sample 2D surface w punkcie (px,py); zwraca null gdy poza obszarem lub maska
              const sampleSurface2D = (surface, xC, yC, px, py) => {
                const minX = xC[0], maxX = xC[xC.length - 1];
                const minY = yC[0], maxY = yC[yC.length - 1];
                if (px < minX || px > maxX || py < minY || py > maxY) return null;
                const stepX = (maxX - minX) / (xC.length - 1);
                const stepY = (maxY - minY) / (yC.length - 1);
                let i = Math.floor((px - minX) / stepX);
                let j = Math.floor((py - minY) / stepY);
                if (i >= xC.length - 1) i = xC.length - 2;
                if (j >= yC.length - 1) j = yC.length - 2;
                const u = (px - xC[i]) / stepX;
                const v = (py - yC[j]) / stepY;
                const v00 = surface[j][i], v10 = surface[j][i + 1];
                const v01 = surface[j + 1][i], v11 = surface[j + 1][i + 1];
                if (v00 == null || v10 == null || v01 == null || v11 == null) return null;
                return (1 - u) * (1 - v) * v00 + u * (1 - v) * v10 + (1 - u) * v * v01 + u * v * v11;
              };

              // Wyznacza ślad profilu w XY (unikatowe XY posortowane wzdłuż głównej osi PCA)
              const computeProfileTrack = (file) => {
                const seen = new Set();
                const pts = [];
                for (let i = 0; i < file.x.length; i++) {
                  const key = `${Math.round(file.x[i])}_${Math.round(file.y[i])}`;
                  if (seen.has(key)) continue;
                  seen.add(key);
                  pts.push({ x: file.x[i], y: file.y[i] });
                }
                if (pts.length < 2) return [];
                let mx = 0, my = 0;
                for (const p of pts) { mx += p.x; my += p.y; }
                mx /= pts.length; my /= pts.length;
                let sxx = 0, sxy = 0, syy = 0;
                for (const p of pts) {
                  const dx = p.x - mx, dy = p.y - my;
                  sxx += dx * dx; sxy += dx * dy; syy += dy * dy;
                }
                // Eigenvector największej wartości własnej macierzy 2x2
                const tr = sxx + syy;
                const det = sxx * syy - sxy * sxy;
                const lambda1 = tr / 2 + Math.sqrt(Math.max(0, tr * tr / 4 - det));
                let dx, dy;
                if (Math.abs(sxy) > 1e-9) { dx = lambda1 - syy; dy = sxy; }
                else { if (sxx >= syy) { dx = 1; dy = 0; } else { dx = 0; dy = 1; } }
                const len = Math.sqrt(dx * dx + dy * dy) || 1;
                dx /= len; dy /= len;
                pts.forEach(p => { p.t = (p.x - mx) * dx + (p.y - my) * dy; });
                pts.sort((a, b) => a.t - b.t);
                return pts;
              };

              // Generuje czerwoną polilinię per plik — TYLKO tam gdzie profil geofizyczny faktycznie ma
              // punkty na rzędnej zbliżonej do stropu skały (część wspólna, nie tylko ślad).
              const TOL_Z_SKALA = 2; // tolerancja w metrach: punkt geofizyki uznajemy za "na stropie skały" gdy |Z_pkt - Z_skały| < TOL
              const profileIntersectionTraces = showProfileIntersections
                ? geoPhysTraces.flatMap(f => {
                    if (!f.visible || f.x.length === 0) return [];
                    const track = computeProfileTrack(f);
                    if (track.length < 2) return [];

                    // Mapa XY → lista Z punktów geofizyki w tej kolumnie pseudo-sekcji
                    const xyzMap = new Map();
                    for (let i = 0; i < f.x.length; i++) {
                      const key = `${Math.round(f.x[i])}_${Math.round(f.y[i])}`;
                      if (!xyzMap.has(key)) xyzMap.set(key, []);
                      xyzMap.get(key).push(f.z[i]);
                    }

                    const xs = [], ys = [], zs = [];
                    for (const p of track) {
                      const zSkala = sampleSurface2D(gridData.skalaSurface, gridData.xCoords, gridData.yCoords, p.x, p.y);
                      if (zSkala == null) continue;
                      const key = `${Math.round(p.x)}_${Math.round(p.y)}`;
                      const zsAt = xyzMap.get(key);
                      if (!zsAt) continue;
                      // Sprawdź czy w tej kolumnie geofizyki jest punkt blisko stropu skały
                      const hasNear = zsAt.some(z => Math.abs(z - zSkala) < TOL_Z_SKALA);
                      if (hasNear) {
                        xs.push(p.x); ys.push(p.y); zs.push(zSkala);
                      }
                    }
                    if (xs.length < 2) return [];
                    return [{
                      type: 'scatter3d', mode: 'lines',
                      x: xs, y: ys, z: zs,
                      line: { color: '#dc2626', width: 7 },
                      name: `🔴 ${f.fileName} ∩ strop skały`,
                      showlegend: true,
                      legendgroup: `profint-${f.id}`,
                      hovertemplate: `<b>Część wspólna profilu i stropu skały</b><br>X: %{x:.1f}<br>Y: %{y:.1f}<br>Z: %{z:.2f} m n.p.m.<extra></extra>`,
                    }];
                  })
                : [];

              return (
              <div>
                <Plot
                  data={sanitizePlotData([
                    {
                      type: 'surface',
                      x: gridData.xCoords,
                      y: gridData.yCoords,
                      z: gridData.gruntSurface,
                      name: 'Strop gruntu (teren)',
                      colorscale: [[0, '#6B5B00'], [1, '#D4A017']],
                      opacity: 0.85,
                      showscale: false,
                      showlegend: true,
                      legendgroup: 'grunt',
                      hovertemplate: 'X: %{x:.1f}<br>Y: %{y:.1f}<br>Z: %{z:.2f} m n.p.m.<br><b>Strop gruntu</b><extra></extra>',
                    },
                    {
                      type: 'surface',
                      x: gridData.xCoords,
                      y: gridData.yCoords,
                      z: displayZwietSurface,
                      name: 'Strop zwietrzeliny',
                      colorscale: [[0, '#B8860B'], [1, '#FFD700']],
                      opacity: 0.7,
                      showscale: false,
                      showlegend: true,
                      legendgroup: 'zwietrzelina',
                      connectgaps: false,
                      hovertemplate: 'X: %{x:.1f}<br>Y: %{y:.1f}<br>Z: %{z:.2f} m n.p.m.<br><b>Strop zwietrzeliny</b><extra></extra>',
                    },
                    {
                      type: 'surface',
                      x: gridData.xCoords,
                      y: gridData.yCoords,
                      z: displaySkalaSurface,
                      name: 'Strop skaly',
                      colorscale: [[0, '#505050'], [1, '#A0A0A0']],
                      opacity: 0.7,
                      showscale: false,
                      showlegend: true,
                      legendgroup: 'skala',
                      connectgaps: false,
                      hovertemplate: 'X: %{x:.1f}<br>Y: %{y:.1f}<br>Z: %{z:.2f} m n.p.m.<br><b>Strop skaly</b><extra></extra>',
                    },
                    // Dane geofizyczne — wszystkie pliki używają wspólnej skali kolorów + jednego colorbara.
                    // Tylko pierwszy widoczny ma showscale=true, reszta = false (żeby nie duplikować colorbara).
                    ...(() => {
                      const visibleTraces = geoPhysTraces.filter(t => t.visible && t.x.length > 0);
                      return visibleTraces.map((f, fIdx) => ({
                        type: 'scatter3d',
                        mode: 'markers',
                        x: f.x,
                        y: f.y,
                        z: f.z,
                        name: `📊 ${f.fileName}`,
                        marker: {
                          size: f.pointSize,
                          color: f.value,
                          colorscale: geoPhysColorscale,
                          opacity: f.opacity,
                          showscale: fIdx === 0, // jeden colorbar dla całej grupy
                          cmin: geoPhysFilterMin,
                          cmax: geoPhysFilterMax,
                          colorbar: fIdx === 0 ? {
                            title: 'Geofizyka',
                            titlefont: { size: 10 },
                            thickness: 12,
                            len: 0.5,
                            x: 1.05,
                          } : undefined,
                        },
                        showlegend: true,
                        legendgroup: `geofiz-${f.id}`,
                        hovertemplate: `<b>${f.fileName}</b><br>X: %{x:.2f}<br>Y: %{y:.2f}<br>Z: %{z:.2f} m n.p.m.<br>Wartość: %{marker.color:.2f}<extra></extra>`,
                      }));
                    })(),
                    // Czerwone polilinie — przecięcia śladu profilu z warstwami (grunt/zwietrzlina/skała)
                    ...profileIntersectionTraces,
                    // Woda — niebieska półprzezroczysta powierzchnia.
                    // uirevision własne dla wody, dzięki czemu user-toggle w legendzie
                    // utrzymuje się mimo zmiany crossSectionMode (które re-renderuje layout).
                    ...(gridData.waterSurface ? [{
                      type: 'surface',
                      x: gridData.xCoords,
                      y: gridData.yCoords,
                      z: gridData.waterSurface,
                      name: 'Zwierciadło wody',
                      colorscale: [[0, '#1565C0'], [1, '#64B5F6']],
                      opacity: 0.5,
                      showscale: false,
                      showlegend: true,
                      legendgroup: 'woda',
                      visible: 'legendonly',
                      uirevision: 'water-trace',
                      connectgaps: false,
                      hovertemplate: 'X: %{x:.1f}<br>Y: %{y:.1f}<br>Z: %{z:.2f} m n.p.m.<br><b>Zwierciadło wody</b><extra></extra>',
                    }] : []),
                    // Otwory jako walce — kolorowe segmenty stref
                    ...(() => {
                      // Sanitizacja: gl-plot3d crashuje gdy scatter3d dostanie NaN/undefined w x/y/z.
                      // Otwory z brakującymi danymi (np. niedoinicjowane warstwy w starszych projektach)
                      // produkowały NaN-owe segmenty → "Cannot read properties of undefined (reading '0')".
                      const safeBoreholes = gridData.boreholeData.filter(b =>
                        Number.isFinite(b.x) && Number.isFinite(b.y) && Number.isFinite(b.elevation)
                        && Number.isFinite(b.gruntBottom) && Number.isFinite(b.zwietrzelinaBottom)
                      );
                      // Znajdź pierwszy otwór z każdą strefą — dla legendy
                      const firstGrunt = safeBoreholes.findIndex(b => b.gruntBottom > 0);
                      const firstZwiet = safeBoreholes.findIndex(b => b.hasZwietrzelina && b.zwietrzelinaBottom > b.gruntBottom);
                      const firstSkala = safeBoreholes.findIndex(b => b.hasSkala);
                      return safeBoreholes.flatMap((b, bIdx) => {
                      const top = b.elevation;
                      const gruntEnd = top - b.gruntBottom;
                      const zwietEnd = top - b.zwietrzelinaBottom;
                      const skalaEnd = zwietEnd - 10;
                      const segments = [];
                      // Segment gruntu
                      if (b.gruntBottom > 0) {
                        segments.push({
                          type: 'scatter3d', mode: 'lines',
                          x: [b.x, b.x], y: [b.y, b.y], z: [top, gruntEnd],
                          line: { color: ZONE_COLORS.grunt, width: 12 },
                          name: bIdx === firstGrunt ? 'Grunt (otwór)' : undefined,
                          showlegend: bIdx === firstGrunt, legendgroup: 'otwor-grunt',
                          hovertemplate: `<b>${b.name}</b><br>Grunt: ${top.toFixed(1)} → ${gruntEnd.toFixed(1)} m n.p.m.<extra></extra>`,
                        });
                      }
                      // Segment zwietrzliny
                      if (b.hasZwietrzelina && b.zwietrzelinaBottom > b.gruntBottom) {
                        segments.push({
                          type: 'scatter3d', mode: 'lines',
                          x: [b.x, b.x], y: [b.y, b.y], z: [gruntEnd, zwietEnd],
                          line: { color: ZONE_COLORS.zwietrzelina, width: 12 },
                          name: bIdx === firstZwiet ? 'Zwietrzelina (otwór)' : undefined,
                          showlegend: bIdx === firstZwiet, legendgroup: 'otwor-zwiet',
                          hovertemplate: `<b>${b.name}</b><br>Zwietrzelina: ${gruntEnd.toFixed(1)} → ${zwietEnd.toFixed(1)} m n.p.m.<extra></extra>`,
                        });
                      }
                      // Segment skały
                      if (b.hasSkala) {
                        segments.push({
                          type: 'scatter3d', mode: 'lines',
                          x: [b.x, b.x], y: [b.y, b.y], z: [zwietEnd, skalaEnd],
                          line: { color: ZONE_COLORS.skala, width: 12 },
                          name: bIdx === firstSkala ? 'Skała (otwór)' : undefined,
                          showlegend: bIdx === firstSkala, legendgroup: 'otwor-skala',
                          hovertemplate: `<b>${b.name}</b><br>Skała: od ${zwietEnd.toFixed(1)} m n.p.m.<extra></extra>`,
                        });
                      }
                      // Etykieta na górze + marker — etykieta opcjonalna (toggle), marker zawsze
                      segments.push({
                        type: 'scatter3d', mode: showBoreholeNames ? 'markers+text' : 'markers',
                        x: [b.x], y: [b.y], z: [top + 1],
                        text: showBoreholeNames ? [b.name] : undefined,
                        textposition: 'top center',
                        textfont: { size: 9, color: '#333' },
                        marker: { size: 3, color: '#e11d48', symbol: 'diamond' },
                        showlegend: false,
                        hoverinfo: 'skip',
                      });
                      return segments;
                    });
                    })(),
                    // Markery A/B, linia przekroju i nóżki — dodawane tylko gdy realnie potrzebne.
                    // Wcześniejszy wariant "zawsze obecne z placeholderami" crashował gl-plot3d
                    // (project: cannot read [0] of undefined) niezależnie od wartości placeholderów.
                    // Kamera 3D jest trzymana przez savedCameraRef + onRelayout, więc zmiana liczby
                    // trace'ów nie resetuje już widoku.
                    ...(() => {
                      if (!crossSectionPointA) return [];

                      // Max elevation gridu (fallback gdy cała siatka zamaskowana)
                      let maxElev = -Infinity;
                      for (const row of gridData.elevationSurface) {
                        if (!row) continue;
                        for (const v of row) {
                          if (v != null && Number.isFinite(v) && v > maxElev) maxElev = v;
                        }
                      }
                      if (!Number.isFinite(maxElev)) maxElev = 0;
                      const lineZ = maxElev + 2;

                      const xs = gridData.xCoords, ys = gridData.yCoords;
                      const getTerrainZ = (pt) => {
                        const yIdx = ys.findIndex(v => v >= pt.y);
                        const xIdx = xs.findIndex(v => v >= pt.x);
                        const yi = Math.min(ys.length - 1, Math.max(0, yIdx === -1 ? ys.length - 1 : yIdx));
                        const xi = Math.min(xs.length - 1, Math.max(0, xIdx === -1 ? xs.length - 1 : xIdx));
                        const v = gridData.elevationSurface[yi]?.[xi];
                        return (v != null && Number.isFinite(v)) ? v : maxElev;
                      };

                      const traces = [];

                      // Markery A (+ B jeśli ustawiony)
                      const markerX = [crossSectionPointA.x];
                      const markerY = [crossSectionPointA.y];
                      const markerZ = [lineZ];
                      const markerT = ['A'];
                      if (crossSectionPointB) {
                        markerX.push(crossSectionPointB.x);
                        markerY.push(crossSectionPointB.y);
                        markerZ.push(lineZ);
                        markerT.push('B');
                      }
                      traces.push({
                        type: 'scatter3d',
                        mode: 'markers+text',
                        x: markerX, y: markerY, z: markerZ,
                        text: markerT,
                        textposition: 'top center',
                        textfont: { size: 14, color: '#7c3aed', family: 'Arial Black' },
                        marker: { size: 10, color: '#7c3aed', symbol: 'cross' },
                        name: 'Przekroj A→B',
                        showlegend: true,
                        hoverinfo: 'skip',
                      });

                      // Nóżka A
                      const terrainZA = getTerrainZ(crossSectionPointA);
                      traces.push({
                        type: 'scatter3d', mode: 'lines',
                        x: [crossSectionPointA.x, crossSectionPointA.x],
                        y: [crossSectionPointA.y, crossSectionPointA.y],
                        z: [lineZ, terrainZA],
                        line: { color: '#7c3aed', width: 3, dash: 'dash' },
                        showlegend: false, hoverinfo: 'skip',
                      });

                      // Linia pozioma A→B + nóżka B (tylko gdy B ustawione)
                      if (crossSectionPointB) {
                        traces.push({
                          type: 'scatter3d', mode: 'lines',
                          x: [crossSectionPointA.x, crossSectionPointB.x],
                          y: [crossSectionPointA.y, crossSectionPointB.y],
                          z: [lineZ, lineZ],
                          line: { color: '#7c3aed', width: 6 },
                          name: 'Linia przekroju',
                          showlegend: false, hoverinfo: 'skip',
                        });
                        const terrainZB = getTerrainZ(crossSectionPointB);
                        traces.push({
                          type: 'scatter3d', mode: 'lines',
                          x: [crossSectionPointB.x, crossSectionPointB.x],
                          y: [crossSectionPointB.y, crossSectionPointB.y],
                          z: [lineZ, terrainZB],
                          line: { color: '#7c3aed', width: 3, dash: 'dash' },
                          showlegend: false, hoverinfo: 'skip',
                        });
                      }

                      return traces;
                    })(),
                  ], 'grid3D')}
                  ref={gridPlotRef}
                  onClick={handlePlotClick}
                  onRelayout={(update) => {
                    if (update['scene.camera']) {
                      savedCameraRef.current = JSON.parse(JSON.stringify(update['scene.camera']));
                    }
                  }}
                  layout={{
                    uirevision: 'keep-camera',
                    title: {
                      text: crossSectionMode
                        ? (crossSectionPointA ? 'Kliknij punkt B na powierzchni' : 'Kliknij punkt A na powierzchni')
                        : 'Powierzchnie stropów warstw geologicznych',
                      font: { size: 14, color: crossSectionMode ? '#7c3aed' : undefined },
                    },
                    scene: (() => {
                      // Proporcjonalne aspectratio — żeby prostokąt był prostokątem, a nie wymuszonym kwadratem
                      const dataRangeX = gridData.xCoords[gridData.xCoords.length - 1] - gridData.xCoords[0];
                      const dataRangeY = gridData.yCoords[gridData.yCoords.length - 1] - gridData.yCoords[0];
                      const dataMax = Math.max(dataRangeX, dataRangeY) || 1;
                      return {
                        uirevision: 'keep-camera-scene',
                        xaxis: { title: 'X (PL-2000)' },
                        yaxis: { title: 'Y (PL-2000)' },
                        zaxis: { title: 'Z (m n.p.m.)' },
                        aspectmode: 'manual',
                        aspectratio: {
                          x: aspectXY * (dataRangeX / dataMax),
                          y: aspectXY * (dataRangeY / dataMax),
                          z: aspectZ,
                        },
                        camera: savedCameraRef.current || {
                          eye: { x: 1.5, y: 1.5, z: 1.2 },
                        },
                      };
                    })(),
                    legend: {
                      x: 0,
                      y: 1,
                      bgcolor: 'rgba(255,255,255,0.8)',
                      font: { size: 11 },
                    },
                    margin: { l: 0, r: 0, t: 40, b: 0 },
                    autosize: true,
                    height: gridIsFullscreen ? window.innerHeight - 280 : 600,
                  }}
                  config={{
                    responsive: true,
                    displayModeBar: true,
                    displaylogo: false,
                    modeBarButtonsToRemove: ['toImage'],
                    modeBarButtonsToAdd: [
                      {
                        name: 'Pobierz PNG',
                        icon: {
                          width: 500,
                          height: 600,
                          path: 'M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm76.5 211.3l-128 128c-4.7 4.7-12.3 4.7-17 0l-128-128C21.2 340.9 25.9 328 35.5 328H108V228c0-6.6 5.4-12 12-12h56c6.6 0 12 5.4 12 12v100h72.5c9.6 0 14.3 12.9 7.5 19.3z',
                          transform: 'matrix(0.04 0 0 0.04 0 0)',
                        },
                        click: function (gd) {
                          window.Plotly.downloadImage(gd, { format: 'png', width: 1600, height: 1000, filename: 'grid_3d_geologiczny' });
                        },
                      },
                    ],
                  }}
                  style={{ width: '100%' }}
                  useResizeHandler
                />

                <div className="mt-3 text-xs text-gray-400 flex items-center gap-4 flex-wrap">
                  <span>Grid {gridData.xCoords.length}×{gridData.yCoords.length} = {(gridData.xCoords.length * gridData.yCoords.length).toLocaleString()} punktów</span>
                  <span>|</span>
                  <span>Otworow: {boreholeData.length}</span>
                  <span>|</span>
                  <span>Interpolacja (power=2)</span>
                  <span>|</span>
                  {gridTerrainSource?.source === 'tile' ? (
                    <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 font-medium" title={`Tile: ${gridTerrainSource.tileFileName}`}>
                      ⛰️ Teren: NMT z tile (krok {gridTerrainSource.tileStep} m)
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                      📐 Teren: IDW z otworów
                    </span>
                  )}
                  <span>|</span>
                  <span>Obracaj: LPM, Zoom: scroll, Przesuń: PPM</span>
                </div>

                {/* --- Panel przekroju z modelu 3D --- */}
                <div className="mt-4 border-t pt-4">
                  <h4 className="text-sm font-semibold text-purple-700 mb-3 flex items-center gap-2">
                    Przekrój z modelu 3D → Edytor
                  </h4>

                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <button
                      onClick={() => {
                        readCurrentCamera();
                        setCrossSectionMode(!crossSectionMode);
                        if (!crossSectionMode) {
                          setCrossSectionPointA(null);
                          setCrossSectionPointB(null);
                          setCrossSectionBoreholes(null);
                        }
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                        crossSectionMode
                          ? 'bg-purple-600 text-white ring-2 ring-purple-300'
                          : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                    >
                      {crossSectionMode
                        ? (crossSectionPointA
                          ? (crossSectionPointB ? 'Punkty wybrane (kliknij aby zresetować)' : 'Kliknij punkt B na wykresie...')
                          : 'Kliknij punkt A na wykresie...')
                        : 'Wyznacz linię przekroju'}
                    </button>

                    {crossSectionPointA && (
                      <span className="text-xs text-gray-500">
                        A: ({crossSectionPointA.x.toFixed(1)}, {crossSectionPointA.y.toFixed(1)})
                      </span>
                    )}
                    {crossSectionPointB && (
                      <>
                        <span className="text-xs text-gray-500">
                          B: ({crossSectionPointB.x.toFixed(1)}, {crossSectionPointB.y.toFixed(1)})
                        </span>
                        <span className="text-xs text-gray-400">
                          | {Math.round(Math.sqrt(
                            Math.pow(crossSectionPointB.x - crossSectionPointA.x, 2) +
                            Math.pow(crossSectionPointB.y - crossSectionPointA.y, 2)
                          ))} m
                        </span>
                      </>
                    )}
                  </div>

                  {crossSectionPointA && crossSectionPointB && (
                    <div className="flex items-center gap-3 flex-wrap mb-3">
                      <label className="flex items-center gap-2 text-xs text-gray-700">
                        <span>Punkty kontrolne:</span>
                        <select
                          value={crossSectionCount}
                          onChange={(e) => setCrossSectionCount(Number(e.target.value))}
                          className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                          title="Liczba punktów IDW wzdłuż linii A→B. Więcej = gładszy przekrój."
                        >
                          {[2, 5, 10, 15, 20, 30, 50, 80, 100].map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </label>

                      <button
                        onClick={handleCrossSectionGenerate}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition"
                      >
                        Generuj przekrój
                      </button>

                      {crossSectionBoreholes && crossSectionBoreholes.length > 0 && (
                        <button
                          onClick={handleOpenInEditor}
                          className="px-4 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition flex items-center gap-1"
                        >
                          Otwórz w edytorze ({crossSectionBoreholes.length} pkt)
                        </button>
                      )}
                    </div>
                  )}

                  {/* Podgląd wygenerowanych punktów kontrolnych */}
                  {crossSectionBoreholes && crossSectionBoreholes.length > 0 && (
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="text-xs font-semibold text-purple-700 mb-2">
                        Podgląd punktów kontrolnych przekroju ({crossSectionBoreholes.length}):
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-500 border-b">
                              <th className="text-left py-1 px-2">Nazwa</th>
                              <th className="text-right py-1 px-2">Odl. (m)</th>
                              <th className="text-right py-1 px-2">Rzędna (m)</th>
                              <th className="text-right py-1 px-2">Grunt</th>
                              <th className="text-right py-1 px-2">Zwietrz.</th>
                              <th className="text-right py-1 px-2">Skała</th>
                              <th className="text-right py-1 px-2" style={{ color: ZONE_COLORS.woda }}>💧 (m)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {crossSectionBoreholes.map((bh, i) => {
                              const gruntL = bh.layers.find(l => l.name === 'Grunt');
                              const kwL = bh.layers.find(l => l.name === 'KW');
                              const smL = bh.layers.find(l => l.name === 'SM');
                              return (
                                <tr key={i} className="border-b border-purple-100">
                                  <td className="py-1 px-2 font-medium">{bh.name}</td>
                                  <td className="py-1 px-2 text-right">{bh.distance}</td>
                                  <td className="py-1 px-2 text-right">{bh.depth}</td>
                                  <td className="py-1 px-2 text-right">{gruntL?.thickness || '-'}</td>
                                  <td className="py-1 px-2 text-right">{kwL?.thickness || '-'}</td>
                                  <td className="py-1 px-2 text-right">{smL?.thickness || '-'}</td>
                                  <td className="py-1 px-2 text-right" style={{ color: ZONE_COLORS.woda }}>
                                    {bh.waterDepth !== null && bh.waterDepth !== undefined ? bh.waterDepth.toFixed(1) : '-'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-2 text-xs text-purple-500">
                        Kliknij "Otwórz w edytorze" aby przenieść ten przekrój do pełnego edytora geologicznego.
                      </div>
                    </div>
                  )}
                </div>

                {/* --- Panel cięcia poziomego (plan na rzędnej Z) --- */}
                <div className="mt-4 border-t pt-4">
                  <h4 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                    Cięcie poziome 3D → plan na rzędnej Z
                  </h4>

                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <button
                      onClick={() => setSliceOpen(!sliceOpen)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                        sliceOpen
                          ? 'bg-amber-600 text-white ring-2 ring-amber-300'
                          : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                      }`}
                    >
                      {sliceOpen ? 'Ukryj cięcie poziome' : 'Pokaż cięcie poziome'}
                    </button>
                    {sliceOpen && sliceZRange && (
                      <span className="text-xs text-gray-500">
                        Zakres: {sliceZRange.zMin} … {sliceZRange.zMax} m n.p.m.
                      </span>
                    )}
                  </div>

                  {sliceOpen && sliceZRange && sliceZ !== null && (
                    <>
                      <div className="flex items-center gap-3 flex-wrap mb-3">
                        <label className="text-xs text-gray-600 w-20">Rzędna Z:</label>
                        <input
                          type="range"
                          min={sliceZRange.zMin}
                          max={sliceZRange.zMax}
                          step={0.5}
                          value={sliceZ}
                          onChange={(e) => setSliceZ(Number(e.target.value))}
                          className="flex-1 min-w-[240px]"
                        />
                        <input
                          type="number"
                          value={sliceZ}
                          min={sliceZRange.zMin}
                          max={sliceZRange.zMax}
                          step={0.5}
                          onChange={(e) => setSliceZ(Number(e.target.value))}
                          className="border rounded px-2 py-1 text-sm w-24"
                        />
                        <span className="text-xs text-gray-500">m n.p.m.</span>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        <label className="text-xs text-gray-600">Wygładzanie:</label>
                        <select
                          value={sliceUpsample}
                          onChange={(e) => setSliceUpsample(Number(e.target.value))}
                          className="border rounded px-2 py-1 text-sm"
                        >
                          <option value={1}>Wył. (piksele)</option>
                          <option value={2}>2× (lekkie)</option>
                          <option value={4}>4× (średnie)</option>
                          <option value={8}>8× (mocne)</option>
                        </select>
                        {sliceMatrix && (
                          <span className="text-xs text-gray-400">
                            Raster: {sliceMatrix.xCoords.length}×{sliceMatrix.yCoords.length} ({(sliceMatrix.xCoords.length * sliceMatrix.yCoords.length).toLocaleString()} komórek)
                          </span>
                        )}
                      </div>

                      {sliceMatrix && (
                        <>
                          {/* Pasek narzędziowy: legenda po lewej, akcje po prawej */}
                          <div className="flex items-center justify-between gap-4 mb-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs flex-wrap">
                            {/* Legenda — kolory i liczniki stref */}
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="flex items-center gap-1.5">
                                <span className="w-3.5 h-3.5 inline-block rounded-sm shadow-sm" style={{ background: ZONE_COLORS.grunt }} />
                                <span className="text-gray-700">Grunt</span>
                                <span className="font-mono text-gray-500">{sliceMatrix.counts.grunt.toLocaleString()}</span>
                              </span>
                              <span className="flex items-center gap-1.5">
                                <span className="w-3.5 h-3.5 inline-block rounded-sm shadow-sm" style={{ background: ZONE_COLORS.zwietrzelina }} />
                                <span className="text-gray-700">Zwietrzlina</span>
                                <span className="font-mono text-gray-500">{sliceMatrix.counts.zwietrzelina.toLocaleString()}</span>
                              </span>
                              <span className="flex items-center gap-1.5">
                                <span className="w-3.5 h-3.5 inline-block rounded-sm shadow-sm" style={{ background: ZONE_COLORS.skala }} />
                                <span className="text-gray-700">Skała</span>
                                <span className="font-mono text-gray-500">{sliceMatrix.counts.skala.toLocaleString()}</span>
                              </span>
                              <span className="flex items-center gap-1.5 text-gray-400">
                                <span className="w-3.5 h-3.5 inline-block rounded-sm border border-gray-300 bg-white" />
                                <span>Powietrze</span>
                                <span className="font-mono">{sliceMatrix.counts.powietrze.toLocaleString()}</span>
                              </span>
                            </div>

                            {/* Akcje — wielkość mapy + eksport CSV */}
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="flex items-center gap-2 pr-3 border-r border-gray-300">
                                <span className="text-gray-700">Wielkość mapy</span>
                                <input
                                  type="range"
                                  min={300}
                                  max={1400}
                                  step={20}
                                  value={sliceHeight}
                                  onChange={(e) => setSliceHeight(Number(e.target.value))}
                                  className="w-36"
                                />
                                <span className="font-mono w-14 text-right text-gray-600">{sliceHeight} px</span>
                                <button
                                  type="button"
                                  onClick={() => setSliceHeight(700)}
                                  className="text-xs px-2 py-0.5 rounded border border-gray-300 hover:bg-white"
                                  title="Reset do 700 px"
                                >
                                  ↺
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={downloadSliceCSV}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm"
                                title="Pobierz CSV z X, Y, Z, Typ — gotowe do gridu w QGIS/Surferze/CAD"
                              >
                                📥 Pobierz CSV (grid)
                              </button>
                            </div>
                          </div>

                          <Plot
                            data={[
                              {
                                type: 'heatmap',
                                x: sliceMatrix.xCoords,
                                y: sliceMatrix.yCoords,
                                z: sliceMatrix.matrix,
                                text: sliceMatrix.labels,
                                colorscale: [
                                  [0.0, ZONE_COLORS.grunt],
                                  [0.499, ZONE_COLORS.grunt],
                                  [0.5, ZONE_COLORS.zwietrzelina],
                                  [0.999, ZONE_COLORS.zwietrzelina],
                                  [1.0, ZONE_COLORS.skala],
                                ],
                                zmin: 1,
                                zmax: 3,
                                showscale: false,
                                hoverongaps: false,
                                hovertemplate: 'X: %{x:.1f}<br>Y: %{y:.1f}<br>%{text}<extra></extra>',
                              },
                              {
                                type: 'scatter',
                                mode: 'markers+text',
                                x: gridData.boreholeData.map(b => b.x),
                                y: gridData.boreholeData.map(b => b.y),
                                text: gridData.boreholeData.map(b => b.name),
                                textposition: 'top center',
                                textfont: { size: 9, color: '#111' },
                                marker: { size: 6, color: '#ef4444', line: { color: '#fff', width: 1 } },
                                name: 'Otwory',
                                hovertemplate: '<b>%{text}</b><br>X: %{x:.1f}<br>Y: %{y:.1f}<extra></extra>',
                                showlegend: false,
                              },
                            ]}
                            layout={{
                              height: sliceHeight,
                              margin: { l: 60, r: 20, t: 30, b: 50 },
                              title: { text: `Z = ${sliceZ.toFixed(1)} m n.p.m.`, font: { size: 12 } },
                              xaxis: { title: 'X (m)', scaleanchor: 'y', scaleratio: 1, constrain: 'domain' },
                              yaxis: { title: 'Y (m)' },
                              showlegend: false,
                              uirevision: 'slice-plot',
                            }}
                            config={{ displaylogo: false, responsive: true, modeBarButtonsToRemove: ['lasso2d', 'select2d'] }}
                            style={{ width: '100%' }}
                            useResizeHandler
                          />

                          <div className="mt-2 text-xs text-amber-700">
                            Plan przekroju przez model 3D na stałej rzędnej. Suwak przesuwa cięcie w pionie. Komórki poza terenem (Z &gt; rzędna terenu) zostają puste.
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
              );
            })()}

            {!gridData && !generatingGrid && (
              <div className="flex items-center justify-center bg-gray-50 rounded-lg py-16">
                <p className="text-gray-400 text-sm">
                  Kliknij "Generuj grid 3D" aby utworzyć siatkę interpolacji powierzchni stropów.
                </p>
              </div>
            )}

            {generatingGrid && (
              <div className="flex items-center justify-center bg-gray-50 rounded-lg py-16">
                <p className="text-gray-500 text-sm">Generowanie grida {effectiveResX}×{effectiveResY}...</p>
              </div>
            )}
          </div>
        )}

        {/* ===== SEKCJA WOKSELE 3D ===== */}
        {boreholeData && boreholeData.length >= 3 && (
          <div className="bg-white rounded-lg shadow-lg p-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Layers size={14} className="text-purple-600" />
              Model wokselowy 3D — objętościowa wizualizacja warstw
            </h3>

            {/* Kontrolki */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Rozdzielczość XY:</label>
                <select
                  value={voxelResXY}
                  onChange={(e) => setVoxelResXY(Number(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={15}>15 x 15</option>
                  <option value={20}>20 x 20</option>
                  <option value={25}>25 x 25</option>
                  <option value={30}>30 x 30</option>
                  <option value={40}>40 x 40</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Warstwy Z:</label>
                <select
                  value={voxelResZ}
                  onChange={(e) => setVoxelResZ(Number(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                </select>
              </div>
              <div className="text-xs text-gray-400">
                = {voxelResXY * voxelResXY * voxelResZ} wokseli
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Skala pionowa (Z):</label>
                <input
                  type="range"
                  min="0.1"
                  max="5"
                  step="0.1"
                  value={voxelAspectZ}
                  onChange={(e) => setVoxelAspectZ(Number(e.target.value))}
                  className="w-24"
                />
                <span className="text-xs text-gray-400 w-8">{voxelAspectZ.toFixed(1)}×</span>
              </div>
              <button
                onClick={handleGenerateVoxels}
                disabled={generatingVoxels}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition disabled:opacity-50"
              >
                {generatingVoxels ? 'Generowanie...' : voxelData ? 'Przelicz woksele' : 'Generuj woksele 3D'}
              </button>

              {voxelData && (
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={downloadVoxelCSV}
                    className="px-3 py-1 rounded text-xs bg-gray-700 text-white hover:bg-gray-800"
                  >
                    Pobierz CSV
                  </button>
                  <button
                    onClick={downloadVoxelHTML3D}
                    className="px-3 py-1 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    Pobierz HTML 3D
                  </button>
                </div>
              )}
            </div>

            {/* Statystyki objętości */}
            {voxelData && (
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-400">Wokseli razem</div>
                  <div className="text-lg font-bold text-gray-700">{voxelData.voxels.length.toLocaleString()}</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ background: ZONE_COLORS.grunt + '20' }}>
                  <div className="text-xs" style={{ color: ZONE_COLORS.grunt }}>Grunt</div>
                  <div className="text-lg font-bold" style={{ color: ZONE_COLORS.grunt }}>
                    {voxelData.stats.volumeGrunt.toLocaleString()} m³
                  </div>
                  <div className="text-xs text-gray-400">{voxelData.stats.grunt} wokseli</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ background: ZONE_COLORS.zwietrzelina + '20' }}>
                  <div className="text-xs" style={{ color: ZONE_COLORS.zwietrzelina }}>Zwietrzlina</div>
                  <div className="text-lg font-bold" style={{ color: ZONE_COLORS.zwietrzelina }}>
                    {voxelData.stats.volumeZwietrzelina.toLocaleString()} m³
                  </div>
                  <div className="text-xs text-gray-400">{voxelData.stats.zwietrzelina} wokseli</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ background: ZONE_COLORS.skala + '20' }}>
                  <div className="text-xs" style={{ color: ZONE_COLORS.skala }}>Skala</div>
                  <div className="text-lg font-bold" style={{ color: ZONE_COLORS.skala }}>
                    {voxelData.stats.volumeSkala.toLocaleString()} m³
                  </div>
                  <div className="text-xs text-gray-400">{voxelData.stats.skala} wokseli</div>
                </div>
              </div>
            )}

            {/* Wykres wokselowy */}
            {voxelData && voxelPlotData && (
              <div>
                <Plot
                  ref={voxelPlotRef}
                  onRelayout={(update) => {
                    if (update['scene.camera']) {
                      savedVoxelCameraRef.current = JSON.parse(JSON.stringify(update['scene.camera']));
                    }
                  }}
                  data={sanitizePlotData([
                    ...voxelPlotData,
                    // Powierzchnia wody — półprzezroczysta niebieska, klikalna w legendzie
                    ...(voxelData.waterSurface ? [{
                      type: 'surface',
                      x: voxelData.xCoords,
                      y: voxelData.yCoords,
                      z: voxelData.waterSurface,
                      name: 'Zwierciadło wody',
                      colorscale: [[0, '#1565C0'], [1, '#64B5F6']],
                      opacity: 0.5,
                      showscale: false,
                      showlegend: true,
                      legendgroup: 'woda',
                      visible: 'legendonly',
                      uirevision: 'voxel-water-trace',
                      connectgaps: false,
                      hovertemplate: 'X: %{x:.1f}<br>Y: %{y:.1f}<br>Z: %{z:.2f} m n.p.m.<br><b>Zwierciadło wody</b><extra></extra>',
                    }] : []),
                    // Otwory
                    {
                      type: 'scatter3d',
                      mode: 'markers+text',
                      x: voxelData.boreholeData.map(b => b.x),
                      y: voxelData.boreholeData.map(b => b.y),
                      z: voxelData.boreholeData.map(b => b.elevation),
                      text: voxelData.boreholeData.map(b => b.name),
                      textposition: 'top center',
                      textfont: { size: 10, color: '#e11d48' },
                      marker: {
                        size: 7,
                        color: '#e11d48',
                        symbol: 'diamond',
                        line: { width: 1, color: '#be123c' },
                      },
                      name: 'Otwory',
                      hovertemplate: '<b>%{text}</b><br>X: %{x:.1f}<br>Y: %{y:.1f}<br>Z: %{z:.2f} m n.p.m.<extra></extra>',
                    },
                  ], 'voxel3D')}
                  layout={{
                    uirevision: 'keep-camera-voxel',
                    title: {
                      text: 'Model wokselowy — warstwy geologiczne',
                      font: { size: 14 },
                    },
                    scene: (() => {
                      // Proporcjonalne aspectratio dla wokseli — żeby prostokąt był prostokątem
                      const dataRangeX = voxelData.xCoords[voxelData.xCoords.length - 1] - voxelData.xCoords[0];
                      const dataRangeY = voxelData.yCoords[voxelData.yCoords.length - 1] - voxelData.yCoords[0];
                      const dataMax = Math.max(dataRangeX, dataRangeY) || 1;
                      return {
                        uirevision: 'keep-camera-voxel-scene',
                        xaxis: { title: 'X (PL-2000)' },
                        yaxis: { title: 'Y (PL-2000)' },
                        zaxis: { title: 'Z (m n.p.m.)' },
                        aspectmode: 'manual',
                        aspectratio: {
                          x: dataRangeX / dataMax,
                          y: dataRangeY / dataMax,
                          z: voxelAspectZ,
                        },
                        camera: savedVoxelCameraRef.current || {
                          eye: { x: 1.5, y: 1.5, z: 1.2 },
                        },
                      };
                    })(),
                    legend: {
                      x: 0,
                      y: 1,
                      bgcolor: 'rgba(255,255,255,0.8)',
                      font: { size: 11 },
                    },
                    margin: { l: 0, r: 0, t: 40, b: 0 },
                    height: 650,
                  }}
                  config={{
                    responsive: true,
                    displayModeBar: true,
                    displaylogo: false,
                  }}
                  style={{ width: '100%' }}
                  useResizeHandler
                />

                <div className="mt-3 text-xs text-gray-400 flex items-center gap-4">
                  <span>Woksele: {voxelResXY}x{voxelResXY}x{voxelResZ}</span>
                  <span>|</span>
                  <span>Rozmiar woksela: ~{voxelData.stats.voxelVolume} m³</span>
                  <span>|</span>
                  <span>Obracaj: LPM, Zoom: scroll, Przesuń: PPM</span>
                </div>
              </div>
            )}

            {!voxelData && !generatingVoxels && (
              <div className="flex items-center justify-center bg-gray-50 rounded-lg py-16">
                <p className="text-gray-400 text-sm">
                  Kliknij "Generuj woksele 3D" aby utworzyć objętościowy model warstw geologicznych.
                </p>
              </div>
            )}

            {generatingVoxels && (
              <div className="flex items-center justify-center bg-gray-50 rounded-lg py-16">
                <p className="text-gray-500 text-sm">Generowanie {voxelResXY * voxelResXY * voxelResZ} wokseli...</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
